import OpenAI from 'openai';
import type { AnalyzeOptions, GemmetricLiteScanResult, OrganizationKey, StrategyOptions } from '../types/organizations';
import { DEFAULT_ORGANIZATION } from '../types/organizations';
import {
  calculateGemmetricCandidateScore,
  generateGemmetricReasoning,
  generateGemmetricRecommendations
} from './gemmetricScoring';

// Lazy initialize OpenAI only if a browser key exists to avoid runtime crash in prod
let openaiClient: OpenAI | null = null;
const ensureOpenAI = (): OpenAI | null => {
  if (openaiClient) return openaiClient;
  const key = process.env.REACT_APP_OPENAI_API_KEY;
  if (!key) return null; // No client-side key; use proxy path instead
  openaiClient = new OpenAI({
    apiKey: key,
    dangerouslyAllowBrowser: true // ⚠️ Only used when key explicitly provided client-side
  });
  return openaiClient;
};

export interface AIProspectAnalysis {
  score: number;
  reasoning: string;
  confidence: number;
  recommendations: string[];
  analyzedAt: string; // When this analysis was performed
  businessHash: string; // Unique identifier for this business
}

// New interface for cached business data (inputs only)
export interface CachedBusinessData {
  name: string;
  address: string;
  website?: string;
  phone?: string;
  normalizedCategory: string;
  rating?: number;
  reviewCount?: number;
  types: string[];
  businessStatus?: string;
  cachedAt: string;
  modelVersion?: string;
}

export interface ProspectData {
  name: string;
  category: string;
  rating?: number;
  reviewCount?: number;
  address: string;
  distance: number;
  types: string[];
  businessStatus?: string;
  website?: string;
  phone?: string;
}

export class AIService {
  private static instance: AIService;
  private businessCache: Map<string, CachedBusinessData> = new Map(); // Cache business data only
  private cacheExpiryDays = Number(process.env.REACT_APP_AI_CACHE_TTL_DAYS) || 7; // Configurable TTL
  private enableAICache = (process.env.REACT_APP_ENABLE_AI_CACHE || '').toLowerCase() === 'true';
  private modelVersion = process.env.REACT_APP_AI_CACHE_VERSION || 'v1';
  
  private constructor() {
    // Initialize cache loading
    this.loadCache().catch(error => {
      console.warn('Failed to load cache during initialization:', error);
    });
  }
  
  public static getInstance(): AIService {
    if (!AIService.instance) {
      AIService.instance = new AIService();
    }
    return AIService.instance;
  }

  /**
   * Generate a unique hash for a business to use as cache key
   * Prefer place_id if available, fallback to name+address hash
   */
  public generateBusinessHash(prospect: ProspectData, placeId?: string): string {
    // Use place_id if available (most stable)
    if (placeId) {
      return `place_${placeId}`;
    }
    
    // Fallback to name+address hash
    const hashData = `${prospect.name.toLowerCase()}-${prospect.address.toLowerCase()}-${prospect.category}`;
    
    // Handle non-Latin1 characters by encoding them properly
    try {
      // First try to encode as UTF-8, then convert to base64
      const encoder = new TextEncoder();
      const bytes = encoder.encode(hashData);
      const base64 = btoa(String.fromCharCode.apply(null, Array.from(bytes)));
      return base64.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
    } catch (error) {
      // Fallback: use a simple hash for problematic characters
      console.warn('Hash generation failed, using fallback for:', prospect.name);
      const simpleHash = hashData
        .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .toLowerCase();
      return simpleHash.substring(0, 20);
    }
  }

  /**
   * Check if cached business data is still valid
   */
  private isCacheValid(cachedData: CachedBusinessData): boolean {
    const cachedDate = new Date(cachedData.cachedAt);
    const now = new Date();
    const daysDiff = (now.getTime() - cachedDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysDiff >= this.cacheExpiryDays) return false;
    // Invalidate cache if model version changed
    if (cachedData.modelVersion && cachedData.modelVersion !== this.modelVersion) return false;
    return true;
  }

  /**
   * Check if business data has changed significantly (for cache invalidation)
   */
  private hasBusinessDataChanged(cached: CachedBusinessData, current: ProspectData): boolean {
    // Check for significant changes in reviews/rating
    if (cached.reviewCount && current.reviewCount) {
      const reviewDiff = Math.abs(current.reviewCount - cached.reviewCount);
      if (reviewDiff >= 10) return true; // +10 reviews threshold
    }
    
    if (cached.rating && current.rating) {
      const ratingDiff = Math.abs(current.rating - cached.rating);
      if (ratingDiff >= 0.1) return true; // +0.1 stars threshold
    }
    
    // Check for website/phone changes
    if (cached.website !== current.website) return true;
    if (cached.phone !== current.phone) return true;
    
    return false;
  }

  /**
   * Load cached business data from localStorage and Firebase
   */
  private async loadCache(): Promise<void> {
    try {
      // Load from localStorage first
      const cached = localStorage.getItem('raedar_ai_cache');
      if (cached) {
        const cacheData = JSON.parse(cached);
        this.businessCache = new Map(Object.entries(cacheData));
        console.log(`📦 Loaded ${this.businessCache.size} cached business data entries from localStorage`);
      } else {
        console.log(`📦 No localStorage cache found - starting with empty cache`);
        this.businessCache = new Map();
      }
      
      // Avoid bulk-loading entire Firebase cache to reduce read costs.
      // We now fetch businesses incrementally where needed via getBusinessesCached().
      
      console.log(`📦 Total cached business data entries loaded: ${this.businessCache.size}`);
    } catch (error) {
      console.warn('Failed to load AI cache:', error);
      this.businessCache = new Map();
    }
  }

  /**
   * Save business cache to localStorage and Firebase
   */
  private async saveCache(): Promise<void> {
    try {
      const cacheData = Object.fromEntries(this.businessCache);
      localStorage.setItem('raedar_ai_cache', JSON.stringify(cacheData));
      console.log(`💾 Saved ${this.businessCache.size} business data entries to localStorage`);
      
      // Also save to Firebase universal cache (gated by env flag)
      if (this.enableAICache) {
        try {
          const { saveUniversalAICache } = await import('./firebase');
          await saveUniversalAICache(cacheData);
        } catch (firebaseError) {
          console.warn('Failed to save to Firebase cache:', firebaseError);
        }
      } else {
        console.log('⚠️ Firebase AI cache saving disabled (REACT_APP_ENABLE_AI_CACHE != true)');
      }
    } catch (error) {
      console.error('Failed to save AI cache:', error);
    }
  }

  /**
   * Get cached business data if available and valid
   */
  private getCachedBusinessData(prospect: ProspectData, placeId?: string): CachedBusinessData | null {
    const hash = this.generateBusinessHash(prospect, placeId);
    const cached = this.businessCache.get(hash);
    
    if (cached && this.isCacheValid(cached) && !this.hasBusinessDataChanged(cached, prospect)) {
      console.log(`📋 Using cached business data for: ${prospect.name} (from memory cache)`);
      return cached;
    }
    
    return null;
  }

  /**
   * Cache business data (inputs only, not analysis results)
   */
  private async cacheBusinessData(prospect: ProspectData, placeId?: string): Promise<void> {
    const hash = this.generateBusinessHash(prospect, placeId);
    const businessData: CachedBusinessData = {
      name: prospect.name,
      address: prospect.address,
      website: prospect.website,
      phone: prospect.phone,
      normalizedCategory: this.normalizeCategoryGPT5(`${prospect.category} ${prospect.types.join(' ')}`.toLowerCase()),
      rating: prospect.rating,
      reviewCount: prospect.reviewCount,
      types: prospect.types,
      businessStatus: prospect.businessStatus,
      cachedAt: new Date().toISOString(),
      modelVersion: this.modelVersion
    };
    
    this.businessCache.set(hash, businessData);
    await this.saveCache();
    console.log(`💾 Cached business data for: ${prospect.name}`);
  }

  /**
   * Calculate QualitySpread based on current batch scores
   */
  private calculateQualitySpread(scores: number[]): number {
    if (scores.length === 0) return 0.5; // Default if no scores
    
    // Sort scores in descending order
    const sortedScores = [...scores].sort((a, b) => b - a);
    
    // Calculate 75th percentile (top 25% threshold)
    const index75 = Math.floor(sortedScores.length * 0.75);
    const score75 = sortedScores[index75];
    
    // Calculate 25th percentile (bottom 25% threshold)
    const index25 = Math.floor(sortedScores.length * 0.25);
    const score25 = sortedScores[index25];
    
    // QualitySpread = (score75 - score25) / max possible spread
    const maxSpread = 100; // Assuming scores are 0-100
    const qualitySpread = Math.max(0, Math.min(1, (score75 - score25) / maxSpread));
    
    return qualitySpread;
  }

  /**
   * Analyze a single prospect with dynamic score calculation
   */
  public async analyzeProspect(prospect: ProspectData, placeId?: string, currentBatchScores: number[] = []): Promise<AIProspectAnalysis> {
    console.log(`🔍 Analyzing prospect: ${prospect.name}`);
    
    // If universal analysis cache is enabled, attempt to reuse analysis output
    const businessHash = this.generateBusinessHash(prospect, placeId);
    if (this.enableAICache) {
      try {
        const { getUniversalAIAnalysis } = await import('./firebase');
        const cachedAnalysis: any = await getUniversalAIAnalysis(businessHash);
        if (cachedAnalysis && cachedAnalysis.modelVersion === this.modelVersion) {
          // Respect TTL for analysis outputs as well (reuse within TTL)
          const updatedAt = cachedAnalysis.updatedAt?.toDate?.() || new Date(cachedAnalysis.updatedAt || 0);
          const daysDiff = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
          if (daysDiff < this.cacheExpiryDays) {
            return {
              score: cachedAnalysis.score,
              reasoning: cachedAnalysis.reasoning,
              confidence: cachedAnalysis.confidence,
              recommendations: cachedAnalysis.recommendations || [],
              analyzedAt: new Date().toISOString(),
              businessHash
            };
          }
        }
      } catch (e) {
        console.warn('Universal analysis cache read failed:', e);
      }
    }

    // Check for cached business data
    const cachedData = this.getCachedBusinessData(prospect, placeId);
    
    // Use cached data if available, otherwise use current prospect data
    const businessData = cachedData || {
      name: prospect.name,
      address: prospect.address,
      website: prospect.website,
      phone: prospect.phone,
      normalizedCategory: this.normalizeCategoryGPT5(`${prospect.category} ${prospect.types.join(' ')}`.toLowerCase()),
      rating: prospect.rating,
      reviewCount: prospect.reviewCount,
      types: prospect.types,
      businessStatus: prospect.businessStatus
    };
    
    // Cache business data if not already cached
    if (!cachedData) {
      await this.cacheBusinessData(prospect, placeId);
      // Proactively warm incremental cache for this business
      try {
        const { getBusinessesCached } = await import('./firebase');
        await getBusinessesCached([this.generateBusinessHash(prospect, placeId)]);
      } catch {}
    }
    
    // Calculate all scores dynamically (never cached)
    const spectrumFit = this.getSpectrumFitScoreGPT5(businessData.normalizedCategory);
    const presence = this.getPresenceScoreGPT5(prospect, businessData.normalizedCategory);
    const scale = this.getScaleScoreGPT5(prospect, businessData.normalizedCategory);
    const keywords = this.getKeywordScoreGPT5(businessData.name, prospect.category, businessData.types);
    const distance = this.getDistanceScoreGPT5(prospect.distance);
    
    // Calculate QualitySpread from current batch
    const qualitySpread = this.calculateQualitySpread(currentBatchScores);
    
    // Apply Tier-3 dealer adjustments
    const isTier3LocalDealer = this.isTier3LocalDealer(businessData.name, businessData.website, prospect.category);
    let adjustedSpectrumFit = spectrumFit;
    let adjustedPresence = presence;
    
    if (isTier3LocalDealer) {
      adjustedSpectrumFit = Math.min(1.0, spectrumFit + 0.03);
      adjustedPresence = Math.min(0.80, presence + 0.03);
    }
    
    // Calculate final score with fresh components
    const finalScore = 
      (0.45 * adjustedSpectrumFit) +   // SpectrumFit (45% weight)
      (0.20 * adjustedPresence) +      // Presence (20% weight)
      (0.10 * scale) +                 // Scale (10% weight)
      (0.09 * keywords) +              // Keywords (9% weight)
      (0.06 * distance) +              // Distance (6% weight) - RECALCULATED EVERY SCAN
      (0.10 * qualitySpread);          // QualitySpread (10% weight) - FROM CURRENT BATCH
    
    const roundedScore = Math.min(100, Math.round(finalScore * 100));
    
    console.log(`📊 Final score for ${prospect.name}: ${roundedScore}`);
    console.log(`   SpectrumFit: ${(adjustedSpectrumFit * 100).toFixed(1)}`);
    console.log(`   Presence: ${(adjustedPresence * 100).toFixed(1)}`);
    console.log(`   Scale: ${(scale * 100).toFixed(1)}`);
    console.log(`   Keywords: ${(keywords * 100).toFixed(1)}`);
    console.log(`   Distance: ${(distance * 100).toFixed(1)} (${prospect.distance.toFixed(2)} mi)`);
    console.log(`   QualitySpread: ${(qualitySpread * 100).toFixed(1)}`);
    
         // Calculate confidence based on data quality and completeness
     const confidence = this.calculateConfidence(prospect, businessData as CachedBusinessData);
    
    const analysis: AIProspectAnalysis = {
      score: roundedScore,
      reasoning: this.generateAdvancedReasoning(prospect, roundedScore),
      confidence,
      recommendations: this.generateAdvancedRecommendations(prospect, roundedScore),
      analyzedAt: new Date().toISOString(),
      businessHash: this.generateBusinessHash(prospect, placeId)
    };

    // Save analysis to universal cache (gated)
    if (this.enableAICache) {
      try {
        const { saveUniversalAIAnalyses } = await import('./firebase');
        await saveUniversalAIAnalyses({
          [businessHash]: {
            ...analysis,
            modelVersion: this.modelVersion
          }
        });
      } catch (e) {
        console.warn('Universal analysis cache write failed:', e);
      }
    }

    return analysis;
  }

  /**
   * Analyze multiple prospects with batch-aware QualitySpread
   */
  public async analyzeProspectsBatch(
    prospects: ProspectData[],
    placeIds?: string[],
    options?: AnalyzeOptions
  ): Promise<AIProspectAnalysis[]> {
    const orgKey = options?.orgKey || DEFAULT_ORGANIZATION;
    if (orgKey === 'gemmetric') {
      return this.analyzeProspectsBatchGemmetric(prospects, placeIds);
    }

    console.log(`🔍 Analyzing ${prospects.length} prospects in batch`);
    
    // Try to read any valid analyses from universal cache first
    const prefilled: (AIProspectAnalysis | null)[] = Array(prospects.length).fill(null);
    const payloadUpdates: Record<string, any> = {}; // Only computed items to persist
    if (this.enableAICache) {
      for (let i = 0; i < prospects.length; i++) {
        try {
          const { getUniversalAIAnalysis } = await import('./firebase');
          const hash = this.generateBusinessHash(prospects[i], placeIds?.[i]);
          const cached: any = await getUniversalAIAnalysis(hash);
          if (cached && cached.modelVersion === this.modelVersion) {
            const updatedAt = cached.updatedAt?.toDate?.() || new Date(cached.updatedAt || 0);
            const daysDiff = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
            if (daysDiff < this.cacheExpiryDays) {
              prefilled[i] = {
                score: cached.score,
                reasoning: cached.reasoning,
                confidence: cached.confidence,
                recommendations: cached.recommendations || [],
                analyzedAt: new Date().toISOString(),
                businessHash: hash
              };
            }
          }
        } catch (e) {
          // proceed with compute
        }
      }
    }

    // First pass: calculate all scores to determine QualitySpread
    const initialScores: number[] = [];
    
    for (let i = 0; i < prospects.length; i++) {
      if (prefilled[i]) { // skip compute for cached analysis
        initialScores.push((prefilled[i] as AIProspectAnalysis).score);
        continue;
      }
      const prospect = prospects[i];
      const placeId = placeIds?.[i];
      
      // Get cached data or use current data
      const cachedData = this.getCachedBusinessData(prospect, placeId);
      const businessData = cachedData || {
        name: prospect.name,
        address: prospect.address,
        website: prospect.website,
        phone: prospect.phone,
        normalizedCategory: this.normalizeCategoryGPT5(`${prospect.category} ${prospect.types.join(' ')}`.toLowerCase()),
        rating: prospect.rating,
        reviewCount: prospect.reviewCount,
        types: prospect.types,
        businessStatus: prospect.businessStatus
      };
      
      // Calculate scores without QualitySpread
      const spectrumFit = this.getSpectrumFitScoreGPT5(businessData.normalizedCategory);
      const presence = this.getPresenceScoreGPT5(prospect, businessData.normalizedCategory);
      const scale = this.getScaleScoreGPT5(prospect, businessData.normalizedCategory);
      const keywords = this.getKeywordScoreGPT5(businessData.name, prospect.category, businessData.types);
      const distance = this.getDistanceScoreGPT5(prospect.distance);
      
      const isTier3LocalDealer = this.isTier3LocalDealer(businessData.name, businessData.website, prospect.category);
      let adjustedSpectrumFit = spectrumFit;
      let adjustedPresence = presence;
      
      if (isTier3LocalDealer) {
        adjustedSpectrumFit = Math.min(1.0, spectrumFit + 0.03);
        adjustedPresence = Math.min(0.80, presence + 0.03);
      }
      
      // Calculate score without QualitySpread
      const scoreWithoutQS = 
        (0.45 * adjustedSpectrumFit) +
        (0.20 * adjustedPresence) +
        (0.10 * scale) +
        (0.09 * keywords) +
        (0.06 * distance);
      
      initialScores.push(scoreWithoutQS * 100);
    }
    
    // Calculate QualitySpread from the batch
    const qualitySpread = this.calculateQualitySpread(initialScores);
    console.log(`📊 Batch QualitySpread: ${(qualitySpread * 100).toFixed(1)}%`);
    
    // Second pass: finalize all analyses with correct QualitySpread
    const analyses: AIProspectAnalysis[] = [];
    
    for (let i = 0; i < prospects.length; i++) {
      if (prefilled[i]) {
        analyses.push(prefilled[i] as AIProspectAnalysis);
        continue;
      }
      const prospect = prospects[i];
      const placeId = placeIds?.[i];
      
      // Get business data for confidence calculation
      const cachedData = this.getCachedBusinessData(prospect, placeId);
      const businessData = cachedData || {
        name: prospect.name,
        address: prospect.address,
        website: prospect.website,
        phone: prospect.phone,
        normalizedCategory: this.normalizeCategoryGPT5(`${prospect.category} ${prospect.types.join(' ')}`.toLowerCase()),
        rating: prospect.rating,
        reviewCount: prospect.reviewCount,
        types: prospect.types,
        businessStatus: prospect.businessStatus
      };
      
      // Cache business data if not already cached
      if (!cachedData) {
        await this.cacheBusinessData(prospect, placeId);
      }
      
      // Calculate final score with correct QualitySpread
      const finalScore = initialScores[i] + (0.10 * qualitySpread * 100);
      const roundedScore = Math.min(100, Math.round(finalScore));
      
      // Calculate confidence based on data quality and completeness
      const confidence = this.calculateConfidence(prospect, businessData as CachedBusinessData);
      
      const analysis: AIProspectAnalysis = {
        score: roundedScore,
        reasoning: this.generateAdvancedReasoning(prospect, roundedScore),
        confidence,
        recommendations: this.generateAdvancedRecommendations(prospect, roundedScore),
        analyzedAt: new Date().toISOString(),
        businessHash: this.generateBusinessHash(prospect, placeId)
      };
      analyses.push(analysis);
      // Track update for persistence (only for newly computed ones)
      if (this.enableAICache) {
        payloadUpdates[analysis.businessHash] = { ...analysis, modelVersion: this.modelVersion };
      }
    }
    
    // Persist computed analyses to universal cache
    if (this.enableAICache) {
      try {
        const { saveUniversalAIAnalyses } = await import('./firebase');
        if (Object.keys(payloadUpdates).length > 0) {
          await saveUniversalAIAnalyses(payloadUpdates);
        } else {
          console.log('✅ No new analyses to save to universal cache');
        }
      } catch (e) {
        console.warn('Universal analysis cache batch write failed:', e);
      }
    }
    
    return analyses;
  }

  /**
   * Gemmetric fast candidate scoring (no website crawl)
   */
  private async analyzeProspectsBatchGemmetric(
    prospects: ProspectData[],
    placeIds?: string[]
  ): Promise<AIProspectAnalysis[]> {
    console.log(`🔍 Gemmetric candidate scoring for ${prospects.length} prospects`);

    const initialScores: number[] = [];
    const businessDataList: Array<{
      name: string;
      normalizedCategory: string;
      website?: string;
      phone?: string;
      rating?: number;
      reviewCount?: number;
    }> = [];

    for (let i = 0; i < prospects.length; i++) {
      const prospect = prospects[i];
      const placeId = placeIds?.[i];
      const cachedData = this.getCachedBusinessData(prospect, placeId);
      const businessData = cachedData || {
        name: prospect.name,
        address: prospect.address,
        website: prospect.website,
        phone: prospect.phone,
        normalizedCategory: this.normalizeCategoryGPT5(`${prospect.category} ${prospect.types.join(' ')}`.toLowerCase()),
        rating: prospect.rating,
        reviewCount: prospect.reviewCount,
        types: prospect.types,
        businessStatus: prospect.businessStatus
      };

      if (!cachedData) {
        await this.cacheBusinessData(prospect, placeId);
      }

      businessDataList.push({
        name: businessData.name,
        normalizedCategory: businessData.normalizedCategory,
        website: businessData.website,
        phone: businessData.phone,
        rating: businessData.rating,
        reviewCount: businessData.reviewCount
      });

      const webPresence = businessData.website ? 0.7 : 0.35;
      const metadata = prospect.address && prospect.category ? 0.65 : 0.4;
      const distance = prospect.distance <= 3 ? 0.85 : 0.55;
      const preliminary =
        0.4 * 0.6 +
        0.25 * webPresence +
        0.15 * metadata +
        0.1 * distance +
        0.1 * 0.5;
      initialScores.push(preliminary * 100);
    }

    const qualitySpread = this.calculateQualitySpread(initialScores);
    const analyses: AIProspectAnalysis[] = [];

    for (let i = 0; i < prospects.length; i++) {
      const prospect = prospects[i];
      const placeId = placeIds?.[i];
      const businessData = businessDataList[i];
      const roundedScore = calculateGemmetricCandidateScore(prospect, businessData, qualitySpread);
      const confidence = this.calculateConfidence(prospect, {
        ...businessData,
        address: prospect.address,
        types: prospect.types,
        cachedAt: new Date().toISOString()
      } as CachedBusinessData);

      analyses.push({
        score: roundedScore,
        reasoning: generateGemmetricReasoning(prospect, roundedScore),
        confidence,
        recommendations: generateGemmetricRecommendations(prospect, roundedScore),
        analyzedAt: new Date().toISOString(),
        businessHash: this.generateBusinessHash(prospect, placeId)
      });
    }

    return analyses;
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { total: number; valid: number; expired: number } {
    let valid = 0;
    let expired = 0;
    
    this.businessCache.forEach((value, key) => {
      if (this.isCacheValid(value)) {
        valid++;
      } else {
        expired++;
      }
    });
    
    return {
      total: this.businessCache.size,
      valid,
      expired
    };
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.businessCache.clear();
    localStorage.removeItem('raedar_ai_cache');
    console.log('🗑️ AI cache cleared');
  }

  private buildAnalysisPrompt(prospect: ProspectData, userPreferences: any): string {
    return `
You are an expert local advertising sales consultant with deep knowledge of industry advertising spending patterns. Analyze this business for LOCAL advertising potential (TV, Streaming TV, Online Video, Display, Search).

CRITICAL: Focus on businesses that buy LOCAL advertising, not national chains.

Business: ${prospect.name}
Category: ${prospect.category}
Rating: ${prospect.rating || 'N/A'} (${prospect.reviewCount || 0} reviews)
Address: ${prospect.address}
Distance: ${prospect.distance.toFixed(1)} miles
Business Types: ${prospect.types.join(', ')}
Status: ${prospect.businessStatus || 'Unknown'}

INDUSTRY-SPECIFIC SCORING (based on real advertising spending data):

TIER 1 - EXCELLENT PROSPECTS (90-100):
- Auto Dealers/Service: Major local advertisers, high TV/streaming spend
- Medical/Dental Practices: Strong local advertising, especially TV and search
- Real Estate Agents/Brokers: Heavy local advertising, especially TV and display
- Legal Services: Good local advertising prospects, especially TV
- Home Services (HVAC, Plumbing, Electrical, Roofing): Strong local advertising - BOOSTED
- Funeral Homes: Surprisingly good local advertisers

TIER 2 - VERY GOOD PROSPECTS (80-89):
- Independent Retail: Good local advertising prospects
- Beauty Salons/Spa: Moderate local advertising
- Fitness Centers/Gyms: Good local advertising prospects
- Insurance Agents: Good local advertising prospects
- Credit Unions: Good local advertising prospects for financial services

TIER 3 - MODERATE PROSPECTS (60-79):
- Local Restaurants: Moderate local advertising, especially TV and streaming
- Local Hotels/Motels: Moderate local advertising
- Independent Pharmacies: Some local advertising
- Local Contractors: Moderate local advertising
- Pet Services: Some local advertising
- Colleges/Universities: Good local advertising prospects for student recruitment and community engagement

TIER 4 - POOR PROSPECTS (20-59):
- Franchises: Varies by brand, some buy local ads
- Local Banks: Rarely buy local advertising
- Government/Public Services: Rarely buy local advertising
- Schools/Universities: Limited local advertising

TIER 5 - NO PROSPECTS (0-19):
- National Chains (Walmart, Walgreens, McDonald's, CVS, etc.): Don't buy local ads
- Gas Stations: Don't buy local advertising
- ATMs: Don't buy local advertising
- Government Agencies: Don't buy local advertising
- Non-profits: Rarely buy local advertising

ADVERTISING TYPES TO RECOMMEND:
- TV advertising (local broadcast) - Best for auto, medical, real estate, legal
- Streaming TV ads - Good for restaurants, retail, services
- Online video advertising - Excellent for all local businesses
- Display advertising - Good for real estate, retail, services
- Search Engine Marketing - Essential for medical, legal, services
- Local radio (if appropriate) - Good for auto, restaurants, retail

User Preferences:
- Target Radius: ${userPreferences.radius} miles
- Preferred Categories: ${userPreferences.preferredCategories?.join(', ') || 'Any'}
- Prospect Count: ${userPreferences.prospectCount}

Please provide:
1. Score (0-100): Use this formula: 45% category weight + 25% distance + 20% presence + 10% keywords - penalties. Auto dealers, dental, legal, HVAC, plumbing, electrical, roofing get 90% category score. National chains get -50 penalty. Gas stations, ATMs get -30 penalty.
2. Reasoning: Explain the scoring breakdown: category value, distance factor, presence signals, and any penalties applied.
3. Confidence (0-100): How confident are you in this assessment?
4. Recommendations: Specific LOCAL advertising approaches (TV, Streaming, Video, Display, Search) based on industry best practices. If score < 30, just say "Not a local advertising prospect."

Respond in JSON format:
{
  "score": 85,
  "reasoning": "Local family-owned restaurant with good reviews - restaurants are excellent prospects for local TV and streaming ads based on industry spending patterns",
  "confidence": 90,
  "recommendations": ["Local TV advertising", "Streaming TV ads", "Online video advertising", "Search Engine Marketing"]
}
    `;
  }

  private parseAIResponse(response: string): AIProspectAnalysis {
    try {
      // Try to extract JSON from the response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          score: Math.min(100, Math.max(0, parsed.score || 50)),
          reasoning: parsed.reasoning || 'Analysis completed',
          confidence: Math.min(100, Math.max(0, parsed.confidence || 50)),
          recommendations: parsed.recommendations || [],
          analyzedAt: new Date().toISOString(),
          businessHash: ''
        };
      }
    } catch (error) {
      console.warn('Failed to parse AI response:', error);
    }
    
    // Fallback parsing
    return {
      score: 50,
      reasoning: response || 'Analysis completed',
      confidence: 50,
      recommendations: [],
      analyzedAt: new Date().toISOString(),
      businessHash: ''
    };
  }

  private fallbackAnalysis(prospect: ProspectData, businessHash: string): AIProspectAnalysis {
    // Advanced scoring system based on GPT-5's recommendations
    const score = this.calculateAdvancedScore(prospect);
    
    return {
      score: Math.min(100, Math.max(0, score)),
      reasoning: this.generateAdvancedReasoning(prospect, score),
      confidence: 85,
      recommendations: this.generateAdvancedRecommendations(prospect, score),
      analyzedAt: new Date().toISOString(),
      businessHash
    };
  }

     /**
    * SpectrumFit Scoring - Channel propensity matrix for Spectrum Reach alignment
    * Implements 4-channel readiness (CTV/OV/Display/SEM) with de-bucketed quality spread
    */
   private calculateAdvancedScore(prospect: ProspectData): number {
     const name = prospect.name.toLowerCase();
     const category = prospect.category.toLowerCase();
     const types = prospect.types.map(t => t.toLowerCase());
     
     // 0. PRE-FILTERS - Drop before scoring
     if (this.isNationalChainGPT5(name, prospect.website, category)) {
       return 0; // National chain hard clamp
     }
     
     if (this.isLowPropensityCategoryGPT5(category, types)) {
       return 0; // Low-propensity category hard clamp
     }
     
     // Auto repair/tire/body ≠ auto dealer
     if (this.isAutoRepairNotDealer(category, types)) {
       return 5; // Very low score for auto repair
     }
     
     // 1. NORMALIZE CATEGORY
     const allCategories = [...category.split(' '), ...types];
     const categoryText = allCategories.join(' ').toLowerCase();
     const normalizedCategory = this.normalizeCategoryGPT5(categoryText);
     
     // 2. COMPONENT SCORES (all 0..1)
     const spectrumFit = this.getSpectrumFitScoreGPT5(normalizedCategory);
     const presence = this.getPresenceScoreGPT5(prospect, normalizedCategory);
     const scale = this.getScaleScoreGPT5(prospect, normalizedCategory);
     const keywords = this.getKeywordScoreGPT5(name, category, types);
     const distance = this.getDistanceScoreGPT5(prospect.distance);
     
     // 3. TIER-3 DEALER NUDGES
     const isTier3LocalDealer = this.isTier3LocalDealer(name, prospect.website, category);
     let adjustedSpectrumFit = spectrumFit;
     let adjustedPresence = presence;
     
     if (isTier3LocalDealer) {
       adjustedSpectrumFit = Math.min(1.0, spectrumFit + 0.03);
       adjustedPresence = Math.min(0.80, presence + 0.03);
     }
     
     // 4. FINAL SCORE CALCULATION (GPT-5 weights)
     const finalScore = 
       (0.45 * adjustedSpectrumFit) +   // SpectrumFit (45% weight)
       (0.20 * adjustedPresence) +      // Presence (20% weight)
       (0.10 * scale) +                 // Scale (10% weight)
       (0.09 * keywords) +              // Keywords (9% weight)
       (0.06 * distance) +              // Distance (6% weight)
       (0.10 * 0.5);                    // Quality spread placeholder (10% weight)
     
     return Math.min(100, Math.round(finalScore * 100));
   }

  /**
   * SpectrumFit Score - 4-channel propensity matrix for Spectrum Reach alignment
   * Maps categories to CTV/OV/Display/SEM readiness with channel-specific weights
   */
  private getSpectrumFitScore(category: string, types: string[]): number {
    const allCategories = [...category.split(' '), ...types];
    const categoryText = allCategories.join(' ').toLowerCase();
    
    // Normalize category names to fix misclassifications
    const normalizedCategory = this.normalizeCategory(categoryText);
    
    // Channel weights for Spectrum Reach mix
    const channelWeights = {
      CTV: 0.40,    // Connected TV (highest weight)
      OV: 0.25,     // Online Video
      Display: 0.15, // Display advertising
      SEM: 0.20     // Search Engine Marketing
    };
    
    // Get 4-channel propensity vector for this category
    const channelPropensity = this.getChannelPropensity(normalizedCategory);
    
    // Calculate weighted SpectrumFit score
    const spectrumFitScore = 
      (channelPropensity.CTV * channelWeights.CTV) +
      (channelPropensity.OV * channelWeights.OV) +
      (channelPropensity.Display * channelWeights.Display) +
      (channelPropensity.SEM * channelWeights.SEM);
    
    return spectrumFitScore;
  }
  
     /**
    * Get 4-channel propensity vector for a category (GPT-5 recipe)
    * Returns readiness scores for CTV/OV/Display/SEM (all 0..1)
    */
   private getChannelPropensity(normalizedCategory: string): { CTV: number; OV: number; Display: number; SEM: number } {
     
     // TIER 1 - EXCELLENT SPECTRUM FIT (Auto, Legal, HVAC, Medical/Dental, Furniture)
     if (normalizedCategory.includes('auto_dealer')) {
       return { CTV: 0.95, OV: 0.90, Display: 0.85, SEM: 0.90 }; // Auto dealers: 0.82-0.85 aggregate
     }
     
     if (normalizedCategory.includes('legal_injury')) {
       return { CTV: 0.90, OV: 0.85, Display: 0.90, SEM: 0.95 }; // Legal (injury): 0.82-0.85 aggregate
     }
     
     if (normalizedCategory.includes('hvac') || normalizedCategory.includes('plumbing') || normalizedCategory.includes('roofing')) {
       return { CTV: 0.90, OV: 0.85, Display: 0.80, SEM: 0.90 }; // HVAC/Plumbing/Roofing: 0.73-0.78 aggregate
     }
     
     if (normalizedCategory.includes('dental') || normalizedCategory.includes('orthodontics')) {
       return { CTV: 0.85, OV: 0.80, Display: 0.75, SEM: 0.95 }; // Dental/Ortho: 0.67-0.72 aggregate
     }
     
     if (normalizedCategory.includes('medical')) {
       return { CTV: 0.85, OV: 0.80, Display: 0.75, SEM: 0.95 }; // Medical: 0.64-0.68 aggregate
     }
     
     if (normalizedCategory.includes('furniture')) {
       return { CTV: 0.85, OV: 0.80, Display: 0.85, SEM: 0.75 }; // Furniture: 0.71-0.75 aggregate
     }
     
     // TIER 2 - GOOD SPECTRUM FIT (Insurance, Fitness, Local Restaurants)
     if (normalizedCategory.includes('insurance')) {
       return { CTV: 0.75, OV: 0.70, Display: 0.70, SEM: 0.80 }; // Insurance good in SEM
     }
     
     if (normalizedCategory.includes('fitness')) {
       return { CTV: 0.70, OV: 0.75, Display: 0.70, SEM: 0.70 }; // Fitness good in OV
     }
     
     if (normalizedCategory.includes('restaurant_fullservice')) {
       return { CTV: 0.45, OV: 0.50, Display: 0.55, SEM: 0.45 }; // Restaurant_fullservice: 0.48-0.52 aggregate
     }
     
     if (normalizedCategory.includes('bar') || normalizedCategory.includes('brewery')) {
       return { CTV: 0.30, OV: 0.35, Display: 0.40, SEM: 0.35 }; // Bar/Brewery: 0.34-0.38 aggregate
     }
     
     if (normalizedCategory.includes('hotel_chain')) {
       return { CTV: 0.20, OV: 0.25, Display: 0.35, SEM: 0.30 }; // Hotel_chain: 0.27-0.30 aggregate
     }
     
     if (normalizedCategory.includes('restaurant_qsr')) {
       return { CTV: 0.10, OV: 0.15, Display: 0.20, SEM: 0.15 }; // QSR_chain: 0.12 aggregate
     }
     
     // TIER 3 - MODERATE SPECTRUM FIT (Real Estate, Other Services)
     if (normalizedCategory.includes('real estate') || normalizedCategory.includes('realtor')) {
       return { CTV: 0.65, OV: 0.60, Display: 0.75, SEM: 0.70 }; // Real estate good in Display
     }
     
     // TIER 4 - POOR SPECTRUM FIT (fallback)
     return { CTV: 0.35, OV: 0.30, Display: 0.35, SEM: 0.40 }; // Low scores for unknown categories
   }

     /**
    * Distance boost (D') - gentle nudge, not a decider (GPT-5 recipe)
    */
   private getDistanceBoost(distance: number, radius: number = 3): number {
     if (distance === null || distance === undefined) return 0.55; // neutral center
     if (distance >= radius) return 0.20; // at radius edge
     
     const m = radius * 0.70; // midpoint where sigmoid = 0.5
     const k = 2.5 / radius; // slope: higher = steeper fall
     
     // raw sigmoid in [0,1]
     const s = 1 / (1 + Math.exp(k * (distance - m)));
     
     // Normalize to 0..1 range
     return (0.20 + 0.60 * s - 0.20) / 0.60;
   }

     /**
    * Scale score - size proxy (GPT-5 recipe)
    */
   private getScaleScore(prospect: ProspectData, normalizedCategory?: string): number {
     // Rating component: clamp01((min(rating,4.8)-3.5)/1.3)
     const ratingPart = prospect.rating ? Math.max(0, Math.min(1, (Math.min(prospect.rating, 4.8) - 3.5) / 1.3)) : 0;
     
     // Per-category review scaling: clamp01(log1p(reviews) / log1p(P95_reviews_in_normCategory))
     const qCat = prospect.reviewCount ? Math.max(0, Math.min(1, Math.log1p(prospect.reviewCount) / Math.max(1e-6, Math.log1p(this.getCategoryReviewPercentile(normalizedCategory))))) : 0;
     
     // Combine: 50% rating + 50% review volume
     return 0.5 * ratingPart + 0.5 * qCat;
   }



     /**
    * Presence score P ∈ [0,1] - Signals the business is market-ready (GPT-5 recipe)
    * Per-category review scaling so restaurants don't dominate
    */
   private getPresenceScore(prospect: ProspectData, normalizedCategory?: string): number {
     // Rating component: clamp01((min(rating,4.8)-3.5)/1.3)
     const ratingPart = prospect.rating ? Math.max(0, Math.min(1, (Math.min(prospect.rating, 4.8) - 3.5) / 1.3)) : 0;
     
     // Per-category review scaling: clamp01(log1p(reviews) / log1p(P95_reviews_in_normCategory))
     const qCat = prospect.reviewCount ? Math.max(0, Math.min(1, Math.log1p(prospect.reviewCount) / Math.max(1e-6, Math.log1p(this.getCategoryReviewPercentile(normalizedCategory))))) : 0;
     
     // Calculate presence score
     let presence = (prospect.website ? 0.25 : 0) + (prospect.phone ? 0.05 : 0) + 0.25 * ratingPart + 0.25 * qCat;
     
     // Nightlife/Hotel dampener (30% reduction)
     if (normalizedCategory && ['bar', 'brewery', 'restaurant_qsr', 'hotel_chain'].includes(normalizedCategory)) {
       presence *= 0.70;
     }
     
     // Cap at 0.80
     return Math.min(presence, 0.80);
   }
  
  /**
   * Get 95th percentile review count for a category (for per-category scaling)
   */
  private getCategoryReviewPercentile(normalizedCategory?: string): number {
    // Per-category 95th percentile review counts (estimated from real data)
    const categoryR95 = {
      'auto_dealer': 800,      // Auto dealers get lots of reviews
      'legal_injury': 600,     // Legal services get good review volume
      'hvac': 400,             // HVAC gets moderate reviews
      'plumbing': 400,         // Plumbing similar to HVAC
      'roofing': 400,          // Roofing similar to HVAC
      'dental': 500,           // Dental gets good reviews
      'orthodontics': 500,     // Ortho similar to dental
      'medical': 600,          // Medical gets good reviews
      'furniture': 300,        // Furniture gets moderate reviews
      'insurance': 200,        // Insurance gets fewer reviews
      'fitness': 400,          // Fitness gets moderate reviews
      'restaurant_fullservice': 800,  // Restaurants get lots of reviews
      'bar': 600,              // Bars get good reviews
      'brewery': 600,          // Breweries similar to bars
      'hotel_chain': 1000,     // Hotels get very high review counts
      'restaurant_qsr': 1200,  // QSR gets extremely high review counts
      'default': 500           // Default for unknown categories
    };
    
    if (!normalizedCategory) return categoryR95.default;
    
    // Find matching category
    for (const [category, r95] of Object.entries(categoryR95)) {
      if (normalizedCategory.includes(category)) {
        return r95;
      }
    }
    
    return categoryR95.default;
  }

     /**
    * Keyword bonus K ∈ [0,1] - Sum of matched hints (cap at 1.0) (GPT-5 recipe)
    */
   private getKeywordScore(name: string, category: string, types: string[]): number {
     const allText = `${name} ${category} ${types.join(' ')}`.toLowerCase();
     let score = 0;
     
     // Legal terms: injury|attorney|trial → +0.25
     const legalTerms = ['injury', 'attorney', 'trial'];
     if (legalTerms.some(term => allText.includes(term))) score += 0.25;
     
     // Auto terms: chevrolet|toyota|ford|kia|hyundai|honda|nissan|auto → +0.20
     const autoTerms = ['chevrolet', 'toyota', 'ford', 'kia', 'hyundai', 'honda', 'nissan', 'auto'];
     if (autoTerms.some(term => allText.includes(term))) score += 0.20;
     
     // Med/Dental: ortho|dental|implant → +0.15
     const medicalTerms = ['ortho', 'dental', 'implant'];
     if (medicalTerms.some(term => allText.includes(term))) score += 0.15;
     
     // Home svcs: hvac|heating|cooling|plumb|roof → +0.15
     const homeServiceTerms = ['hvac', 'heating', 'cooling', 'plumb', 'roof'];
     if (homeServiceTerms.some(term => allText.includes(term))) score += 0.15;
     
     // Furniture: furniture|mattress → +0.10
     const furnitureTerms = ['furniture', 'mattress'];
     if (furnitureTerms.some(term => allText.includes(term))) score += 0.10;
     
     // K = min(sum, 1.0)
     return Math.min(score, 1.0);
   }

     /**
    * National chain detection - Hard clamp to bottom (improved with hostname matching and local dealer allowlist)
    */
   private isNationalChain(name: string, website?: string, category?: string): boolean {
     // Helper function to extract hostname from URL
     const getHostname = (url?: string): string => {
       try {
         return new URL(url || "").hostname.toLowerCase();
       } catch {
         return "";
       }
     };
     
     // Chain hostname patterns (more precise than name matching)
     const chainHosts = [
       /mcdonalds\.com/, /olivegarden\.com/, /chilis\.com/, /ramada\.com/, /ihg\.com/, /hilton\.com/, /marriott\.com/,
       /walmart\.com/, /target\.com/, /homedepot\.com/, /lowes\.com/, /bestbuy\.com/,
       /walgreens\.com/, /cvs\.com/, /publicstorage\.com/, /uhaul\.com/,
       /carmax\.com/, /autonation\.com/, /carvana\.com/, /vroom\.com/, /echopark\.com/,
       /harborfreight\.com/, /goodwill\.org/, /salvationarmy\.org/, /samsclub\.com/, /winn-dixie\.com/,
       /burgerking\.com/, /kfc\.com/, /tacobell\.com/, /subway\.com/, /dominos\.com/, /pizzahut\.com/, 
       /starbucks\.com/, /dunkin\.com/, /wendys\.com/, /arby\.com/, /sonicdrivein\.com/, /hardees\.com/, 
       /carlsjr\.com/, /jackinthebox\.com/, /chipotle\.com/, /qdobas\.com/, /moes\.com/, /panera\.com/,
       /jimmyjohns\.com/, /firehousesubs\.com/, /chickfila\.com/, /popeyes\.com/,
       /krispykreme\.com/, /baskinrobbins\.com/, /dairyqueen\.com/, /whataburger\.com/,
       /innout\.com/, /fiveguys\.com/, /shakeshack\.com/, /shell\.com/, /exxon\.com/,
       /mobil\.com/, /chevron\.com/, /bp\.com/, /texaco\.com/, /sunoco\.com/,
       /bankofamerica\.com/, /wellsfargo\.com/, /chase\.com/, /citibank\.com/, /usbank\.com/
     ];
     
     // Chain word patterns with word boundaries (more precise matching)
     const chainWords = [
       /\bmcdonald'?s?\b/i, /\bolive garden\b/i, /\bhilton\b/i, /\bmarriott\b/i, /\bramada\b/i,
       /\bwalmart\b/i, /\bwalgreens\b/i, /\bcvs\b/i, /\bcarmax\b/i, /\bautonation\b/i,
       /\bburger king\b/i, /\bkfc\b/i, /\btaco bell\b/i, /\bsubway\b/i, /\bdominos\b/i,
       /\bpizza hut\b/i, /\bstarbucks\b/i, /\bdunkin\b/i, /\bwendys\b/i, /\barby\b/i,
       /\bsonic\b/i, /\bhardees\b/i, /\bcarls jr\b/i, /\bjack in the box\b/i,
       /\bchipotle\b/i, /\bqdobas\b/i, /\bmoes\b/i, /\bpanera\b/i, /\bjimmy johns\b/i,
       /\bfirehouse subs\b/i, /\bchick-fil-a\b/i, /\bchick fil a\b/i, /\bpopeyes\b/i,
       /\bkrispy kreme\b/i, /\bbaskin robbins\b/i, /\bdairy queen\b/i, /\bwhataburger\b/i,
       /\bin-n-out\b/i, /\bfive guys\b/i, /\bshake shack\b/i, /\bshell\b/i, /\bexxon\b/i,
       /\bmobil\b/i, /\bchevron\b/i, /\bbp\b/i, /\btexaco\b/i, /\bsunoco\b/i,
       /\bbank of america\b/i, /\bwells fargo\b/i, /\bchase\b/i, /\bcitibank\b/i, /\bus bank\b/i
     ];
     
     // Local dealer allowlist (Tier-3 franchises that should NOT be excluded)
     const localDealerAllow = /\b(serra|hendrick|ed voyles|greenway|rick case|sandy sansing|hall|courtesy)\b/i;
     
     // Tier-3 auto brands
     const tier3Brands = /\b(toyota|chevrolet|ford|honda|kia|hyundai|nissan|jeep|ram|gmc|cadillac|buick|vw|volkswagen|mazda|subaru|bmw|mercedes|lexus|audi|volvo)\b/i;
     
     const nameLower = (name || "").toLowerCase();
     const hostname = getHostname(website);
     
     // Allow local Tier-3 auto franchises
     if (localDealerAllow.test(nameLower) && tier3Brands.test(nameLower)) {
       return false; // Local Tier-3 franchise - keep it
     }
     
     // Check hostname patterns
     if (chainHosts.some(rx => rx.test(hostname))) {
       return true; // Chain detected by hostname
     }
     
     // Check word patterns
     if (chainWords.some(rx => rx.test(nameLower))) {
       return true; // Chain detected by name
     }
     
     return false;
   }
   
   /**
    * Check if this is a Tier-3 local auto dealer (should get a small boost)
    */
   private isTier3LocalDealer(name: string, website?: string, category?: string): boolean {
         const nameLower = (name || "").toLowerCase();
     
     // Local dealer allowlist (Tier-3 franchises that should get a boost)
     const localDealerAllow = /\b(serra|hendrick|ed voyles|greenway|rick case|sandy sansing|hall|courtesy)\b/i;
     
     // Tier-3 auto brands
     const tier3Brands = /\b(toyota|chevrolet|ford|honda|kia|hyundai|nissan|jeep|ram|gmc|cadillac|buick|vw|volkswagen|mazda|subaru|bmw|mercedes|lexus|audi|volvo)\b/i;
     
     // Check if it's a local dealer with a Tier-3 brand
     return localDealerAllow.test(nameLower) && tier3Brands.test(nameLower);
   }

     /**
    * Low-propensity category detection - Hard clamp for categories that don't buy local advertising
    */
   private isLowPropensityCategory(category: string, types: string[]): boolean {
     const lowPropensityCategories = [
       'gas station', 'convenience store', 'storage', 'self storage', 'atm', 'automated teller',
       'government', 'school', 'university', 'college', 'library', 'park',
       'post office', 'shipping', 'utility', 'religious org', 'cemetery', 'car wash',
       'laundromat', 'parking', 'big-box retail', 'dollar store', 'thrift store',
       'pawn shop', 'check cashing', 'pharmacy', 'liquor store', 'hotel', 'motel',
       'fast food', 'quick service', 'qsr', 'drive thru', 'drive-through',
       'bank', 'credit union', 'financial institution', 'nonprofit', 'non-profit', 'charity',
       'foundation', 'donation', 'thrift', 'resale', 'consignment', 'pawn', 'check cashing',
       'payday loan', 'title loan', 'cash advance', 'money order', 'western union', 'moneygram',
       'thrift', 'nonprofit', 'non-profit', 'charity', 'foundation', 'donation'
     ];
    
    const allCategories = [...category.split(' '), ...types];
    return allCategories.some(cat => 
      lowPropensityCategories.some(lpc => cat.toLowerCase().includes(lpc))
    );
  }

     /**
    * Category normalization - Fix misclassifications and map to proper verticals
    */
   private normalizeCategory(categoryText: string): string {
     // Map noisy categories to proper classifications (GPT-5 recipe)
     const categoryMap: { [key: string]: string } = {
       // Auto dealers (keep these)
       'auto dealer': 'auto_dealer',
       'car dealer': 'auto_dealer',
       'automotive': 'auto_dealer',
       'car dealership': 'auto_dealer',
       
       // Legal services
       'lawyer': 'legal_injury',
       'attorney': 'legal_injury',
       'legal': 'legal_injury',
       'personal injury': 'legal_injury',
       'injury lawyer': 'legal_injury',
       'accident': 'legal_injury',
       
       // Home services
       'hvac': 'hvac',
       'heating': 'hvac',
       'air conditioning': 'hvac',
       'cooling': 'hvac',
       'plumbing': 'plumbing',
       'roofing': 'roofing',
       'home services': 'hvac',
       'contractor': 'hvac',
       
       // Medical/Dental
       'dentist': 'dental',
       'dental': 'dental',
       'orthodontist': 'orthodontics',
       'orthodontics': 'orthodontics',
       'medical': 'medical',
       'physician': 'medical',
       'doctor': 'medical',
       'clinic': 'medical',
       'hospital': 'medical',
       'urgent care': 'medical',
       'plastic surgeon': 'medical',
       'surgeon': 'medical',
       'ortho': 'orthodontics',
       'implant': 'dental',
       
       // Furniture
       'furniture': 'furniture',
       'furniture store': 'furniture',
       'home furnishings': 'furniture',
       'mattress': 'furniture',
       'sofa': 'furniture',
       
       // Insurance
       'insurance': 'insurance',
       'insurance agent': 'insurance',
       'insurance broker': 'insurance',
       
       // Fitness
       'fitness': 'fitness',
       'gym': 'fitness',
       'health club': 'fitness',
       'personal trainer': 'fitness',
       
       // Restaurants (distinguish QSR vs full-service)
       'cafe': 'restaurant_qsr',
       'meal delivery': 'restaurant_qsr',
       'fast food': 'restaurant_qsr',
       'quick service': 'restaurant_qsr',
       'drive thru': 'restaurant_qsr',
       'drive-through': 'restaurant_qsr',
       'qsr': 'restaurant_qsr',
       'restaurant': 'restaurant_fullservice',
       'dining': 'restaurant_fullservice',
       'food': 'restaurant_fullservice',
       
       // Nightlife
       'bar': 'bar',
       'pub': 'bar',
       'brewery': 'brewery',
       'tavern': 'bar',
       
       // Hotel chains (should be excluded)
       'hotel': 'hotel_chain',
       'motel': 'hotel_chain',
       'inn': 'hotel_chain',
       'lodge': 'hotel_chain',
       
       // Low-propensity categories (should be excluded)
       'convenience store': 'gas station',
       'fuel': 'gas station',
       'self storage': 'storage',
       'public storage': 'storage',
       'automated teller': 'atm',
       'bank branch': 'bank',
       'credit union': 'credit union',
       'post office': 'government',
       'dmv': 'government',
       'library': 'government',
       'park': 'government',
       'religious org': 'thrift_nonprofit',
       'cemetery': 'thrift_nonprofit',
       'car wash': 'automotive',
       'laundromat': 'service',
       'parking': 'service',
       'big-box retail': 'retail',
       'dollar store': 'retail',
       'thrift store': 'thrift_nonprofit',
       'pawn shop': 'retail',
       'check cashing': 'financial',
       'nonprofit': 'thrift_nonprofit',
       'non-profit': 'thrift_nonprofit',
       'charity': 'thrift_nonprofit',
       'foundation': 'thrift_nonprofit',
       'donation': 'thrift_nonprofit',
       'thrift': 'thrift_nonprofit',
       'resale': 'thrift_nonprofit',
       'consignment': 'thrift_nonprofit'
     };
     
     // Check for exact matches first
     for (const [noisy, normalized] of Object.entries(categoryMap)) {
       if (categoryText.includes(noisy)) {
         return normalized;
       }
     }
     
     return categoryText;
   }

  /**
   * Calculate penalties for low-propensity categories (if not hard-clamped)
   */
  private calculatePenalties(name: string, category: string, types: string[]): number {
    let penalties = 0;
    
    // National chain penalty P_chain = 1.0 if chain matched
    if (this.isNationalChain(name)) {
      penalties += 100; // Heavy penalty for national chains
    }
    
    // Low-propensity penalty P_low = 0.6 if category is in the list
    const lowPropensityCategories = [
      'gas station', 'convenience store', 'storage', 'self storage', 'atm', 'automated teller',
      'government', 'school', 'university', 'college', 'library', 'park',
      'post office', 'shipping', 'utility', 'religious org', 'cemetery', 'car wash',
      'laundromat', 'parking', 'big-box retail', 'dollar store', 'thrift store',
      'pawn shop', 'check cashing'
    ];
    
    const allCategories = [...category.split(' '), ...types];
    if (allCategories.some(cat => lowPropensityCategories.some(lpc => cat.toLowerCase().includes(lpc)))) {
      penalties += 60; // Penalty for low-propensity categories
    }
    
    return penalties;
  }

       private generateAdvancedReasoning(prospect: ProspectData, score: number): string {
    const category = prospect.category.toLowerCase();
    const distance = prospect.distance;
    const name = prospect.name;
    
    // Normalize category for consistent processing
    const allCategories = [...category.split(' '), ...prospect.types];
    const categoryText = allCategories.join(' ').toLowerCase();
    const normalizedCategory = this.normalizeCategory(categoryText);
    
    let reasoning = '';
    
    // Start with a simple, friendly explanation
    if (score >= 80) {
      reasoning += `${name} is an excellent prospect for local advertising. `;
    } else if (score >= 60) {
      reasoning += `${name} is a good prospect for local advertising. `;
    } else if (score >= 40) {
      reasoning += `${name} could be a decent prospect for local advertising. `;
    } else {
      reasoning += `${name} may not be the best fit for local advertising. `;
    }
    
    // Category-specific reasoning in plain English
    if (normalizedCategory.includes('auto_dealer')) {
      reasoning += 'Auto dealers typically have good advertising budgets and respond well to TV and streaming ads. ';
    } else if (normalizedCategory.includes('dental') || normalizedCategory.includes('medical')) {
      reasoning += 'Medical and dental practices often need to reach local patients and do well with TV and search marketing. ';
    } else if (normalizedCategory.includes('legal') || normalizedCategory.includes('attorney')) {
      reasoning += 'Law firms often have good budgets for advertising and need to reach local clients. ';
    } else if (normalizedCategory.includes('hvac') || normalizedCategory.includes('plumbing')) {
      reasoning += 'Home service businesses like HVAC and plumbing companies often advertise locally and respond well to TV ads. ';
    } else if (normalizedCategory.includes('restaurant')) {
      reasoning += 'Restaurants benefit from local advertising to attract nearby customers. ';
    }
    
    // Website presence
    if (prospect.website) {
      reasoning += 'They have a website, which shows they understand the importance of online presence. ';
    }
    
    // Rating and reviews
    if (prospect.rating && prospect.rating >= 4.0) {
      reasoning += `They have a good rating of ${prospect.rating} stars, which suggests they're an established business. `;
    }
    
    if (prospect.reviewCount && prospect.reviewCount >= 100) {
      reasoning += `They have ${prospect.reviewCount} reviews, showing they're well-known in the area. `;
    }
    
    // Distance reasoning in simple terms
    if (distance <= 1) {
      reasoning += `They're very close at ${distance.toFixed(1)} miles away, making them easy to reach. `;
    } else if (distance <= 3) {
      reasoning += `They're nearby at ${distance.toFixed(1)} miles away. `;
    } else {
      reasoning += `They're ${distance.toFixed(1)} miles away, but still within your service area. `;
    }
    
    // Business status
    if (prospect.businessStatus === 'Open') {
      reasoning += 'They appear to be actively operating. ';
    }
    
    return reasoning;
  }

  private generateAdvancedRecommendations(prospect: ProspectData, score: number): string[] {
    if (score < 30) {
      return ['Not a local advertising prospect'];
    }
    
    const recommendations = [];
    const category = prospect.category.toLowerCase();
    
    if (category.includes('auto') || category.includes('car')) {
      recommendations.push('Local TV advertising', 'Streaming TV ads', 'Online video advertising');
    } else if (category.includes('dental') || category.includes('medical')) {
      recommendations.push('Local TV advertising', 'Search Engine Marketing', 'Streaming TV ads');
    } else if (category.includes('legal') || category.includes('attorney')) {
      recommendations.push('Local TV advertising', 'Display advertising', 'Search Engine Marketing');
    } else if (category.includes('hvac') || category.includes('plumbing')) {
      recommendations.push('Local TV advertising', 'Streaming TV ads', 'Search Engine Marketing');
    } else {
      recommendations.push('Local TV advertising', 'Streaming TV ads', 'Online video advertising');
    }
    
    return recommendations;
  }

  /**
   * Pre-filter obvious bad prospects before AI analysis
   */
  private preFilterProspect(prospect: ProspectData): number | null {
    const name = prospect.name.toLowerCase();
    const category = prospect.category.toLowerCase();
    const types = prospect.types.map(t => t.toLowerCase());
    
    // National chains that NEVER buy local advertising
    const nationalChains = [
      'walmart', 'walgreens', 'cvs', 'mcdonalds', 'burger king', 'kfc', 'taco bell',
      'subway', 'dominos', 'pizza hut', 'starbucks', 'dunkin', 'dunkin donuts',
      'target', 'home depot', 'lowes', 'best buy', 'staples', 'office depot',
      'petco', 'petsmart', 'autozone', 'advance auto', 'oreilly', 'napa',
      'shell', 'exxon', 'mobil', 'chevron', 'bp', 'texaco', 'sunoco',
      'bank of america', 'wells fargo', 'chase', 'citibank', 'us bank',
      'regions bank', 'bb&t', 'truist', 'pnc', 'capital one'
    ];
    
    // Gas stations and ATMs
    const gasStations = ['gas station', 'gas', 'fuel', 'convenience store'];
    const atms = ['atm', 'automated teller'];
    
    // Government and non-profits
    const government = ['government', 'city of', 'county of', 'state of', 'federal', 'post office', 'dmv'];
    const nonProfits = ['non-profit', 'nonprofit', 'charity', 'foundation'];
    
    // Check for national chains
    if (nationalChains.some(chain => name.includes(chain))) {
      return 5; // Very low score for national chains
    }
    
    // Check for gas stations
    if (gasStations.some(gas => category.includes(gas) || types.some(t => t.includes(gas)))) {
      return 10; // Low score for gas stations
    }
    
    // Check for ATMs
    if (atms.some(atm => name.includes(atm) || category.includes(atm))) {
      return 5; // Very low score for ATMs
    }
    
    // Check for government/non-profits
    if (government.some(gov => name.includes(gov) || category.includes(gov))) {
      return 15; // Low score for government
    }
    
    if (nonProfits.some(np => name.includes(np) || category.includes(np))) {
      return 20; // Low score for non-profits
    }
    
    // If no obvious red flags, let AI analyze
    return null;
  }
  
  /**
   * Get reasoning for pre-filtered prospects
   */
  private getPreFilterReasoning(prospect: ProspectData): string {
    const name = prospect.name.toLowerCase();
    const category = prospect.category.toLowerCase();
    
    if (name.includes('walmart') || name.includes('walgreens') || name.includes('cvs')) {
      return 'National chain - corporate controls all advertising decisions, local stores do not buy local advertising';
    }
    
    if (name.includes('atm') || category.includes('atm')) {
      return 'ATM machine - not a business that purchases local advertising';
    }
    
    if (category.includes('gas station') || category.includes('convenience store')) {
      return 'Gas station/convenience store - typically do not invest in local advertising';
    }
    
    if (name.includes('government') || name.includes('city of') || name.includes('county of')) {
      return 'Government entity - does not purchase local advertising';
    }
    
    return 'Pre-filtered as poor local advertising prospect';
  }

  /**
   * Spatial diversification - prevents clustering by enforcing minimum separation
   */
  diversifyProspects(prospects: any[], k: number, minSepMi: number = 0.25): any[] {
    const pick: any[] = [];
    
    for (const prospect of prospects) {
      const farEnough = pick.every(p => 
        this.haversineDistance(p.lat, p.lng, prospect.lat, prospect.lng) >= minSepMi
      );
      
      if (farEnough) {
        pick.push(prospect);
      }
      
      if (pick.length === k) break;
    }
    
    // If we didn't hit k, fill remainder ignoring separation
    if (pick.length < k) {
      for (const prospect of prospects) {
        if (!pick.includes(prospect)) {
          pick.push(prospect);
          if (pick.length === k) break;
        }
      }
    }
    
    return pick;
  }

  /**
   * Quantile spread with jitter - Break ties and create continuous quality distribution
   */
  private applyQuantileSpread(analyses: AIProspectAnalysis[], prospects: ProspectData[]): void {
    const aiScores = analyses.map(a => a.score);
    const reviewCounts = prospects.map(p => p.reviewCount || 0);
    
    // Add tiny jitter so equal scores aren't identical (based on review count)
    const jitteredScores = aiScores.map((score, i) => score + 1e-6 * Math.log1p(reviewCounts[i]));
    
    // Create ranking index
    const indexed = jitteredScores.map((score, i) => [score, i]).sort((a, b) => a[0] - b[0]);
    const rankIndex = indexed.map(([, i]) => i);
    
    // Calculate empirical quantiles
    const quantiles = Array(aiScores.length).fill(0);
    for (let rank = 0; rank < rankIndex.length; rank++) {
      quantiles[rankIndex[rank]] = rank / Math.max(1, rankIndex.length - 1); // 0..1
    }
    
    // Apply gamma stretch to exaggerate separation
    const gamma = 1.3;
    const stretchedQuantiles = quantiles.map(q => Math.pow(q, gamma));
    
    // Update scores with continuous distribution
    analyses.forEach((analysis, i) => {
      analysis.score = Math.round(stretchedQuantiles[i] * 100);
    });
    
    console.log(`📊 Quantile spread applied: scores now range from ${Math.min(...stretchedQuantiles.map(q => q * 100))} to ${Math.max(...stretchedQuantiles.map(q => q * 100))}`);
  }

  /**
   * Percentile scaling for UI display - Avoid "bottomed out" appearance
   */
  private applyPercentileScaling(analyses: AIProspectAnalysis[]): void {
    const finalScores = analyses.map(a => a.score);
    const sorted = [...finalScores].sort((a, b) => a - b);
    
    analyses.forEach((analysis, i) => {
      const score = analysis.score;
      const rank = sorted.findIndex(v => v >= score);
      const percentile = rank < 0 ? 1 : rank / Math.max(1, sorted.length - 1);
      
      // Scale to 10-90 range for stable UI display
      const displayScore = Math.round(10 + 80 * percentile);
      
      // Store both the strict ordering score and display score
      analysis.score = score; // Keep original for strict ordering
      (analysis as any).displayScore = displayScore; // Add display score for UI
    });
    
    console.log(`📊 Percentile scaling applied: display scores range from 10 to 90`);
  }

  /**
   * Haversine distance calculation for spatial diversification
   */
  private haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Check if AI service is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const client = ensureOpenAI();
      if (!client) {
        return false;
      }
      
      // Test with a simple prompt
      const response = await client.chat.completions.create({
        model: "gpt-4-turbo",
        messages: [{ role: "user", content: "Hello" }],
        max_tokens: 5
      });
      
      return !!response.choices[0].message.content;
    } catch (error) {
      console.warn('AI service not available:', error);
      return false;
    }
  }

  /**
   * Generate AI-powered appointment strategy for a prospect
   */
  async generateAppointmentStrategy(
    prospect: ProspectData,
    options?: StrategyOptions
  ): Promise<{
    openingLine: string;
    bestTimeToCall: string;
    keyTalkingPoints: string[];
    objectionHandlers: string[];
    valueProposition: string;
    callToAction: string;
    competitiveAngles?: string[];
    verticalInsights?: string[];
    discoveryQuestions?: string[];
    hookLines?: string[];
    mediaMix?: Array<{ channel: string; percent?: number; why: string }> | string[];
    visibilityPriorities?: string[];
    liteScanInsights?: string[];
    plan30Day?: string[];
    introEmail?: string;
    followUpEmail?: string;
    voicemailScript?: string;
  }> {
    const orgKey = options?.orgKey || DEFAULT_ORGANIZATION;
    if (orgKey === 'gemmetric') {
      return this.generateGemmetricAppointmentStrategy(prospect, options?.liteScan);
    }

    try {
      console.log(`🎯 Generating appointment strategy for ${prospect.name}...`);
      
      const prompt = `
You are an expert LOCAL ADVERTISING SALES CONSULTANT. Produce a high-conversion, seller-ready appointment strategy that helps an AE break through on a COLD CALL. Make it specific to the business CATEGORY (no generic advice), with short, punchy bullets and local-market framing.

Business: ${prospect.name}
Category: ${prospect.category}
Rating: ${prospect.rating || 'N/A'} (${prospect.reviewCount || 0} reviews)
Address: ${prospect.address}
Website Present: ${prospect.website ? 'Yes' : 'No'}

Return ONLY valid JSON with this shape (all base fields required):
{
  "openingLine": string,
  "bestTimeToCall": string,
  "keyTalkingPoints": string[],           // 6-8 strong, category-specific bullets that a seller can say verbatim
  "objectionHandlers": string[],          // 6-8 concise scripts (owner objections + seller response)
  "valueProposition": string,             // one-sentence value tied to CATEGORY
  "callToAction": string                  // direct ask to secure a time
}

Add the following enrichment fields (optional but encouraged, 4-8 items each, all category-specific):
- competitiveAngles: positioning against common local competitors
- verticalInsights: operational or marketing realities for this CATEGORY
- discoveryQuestions: specific qualifiers the AE should ask
- hookLines: short hooks for voicemail or walk-in
- mediaMix: ONLY Spectrum Reach channels with why (Linear TV, Streaming TV/CTV, Online Video, Display, Search Engine Marketing). Format: [{"channel":"Linear TV","percent":40,"why":"..."}, ...] with percents totaling 100.
- 30DayPlan: day-by-day or week-by-week action plan for first 30 days
- introEmail: short, skimmable introductory email for first contact
- followUpEmail: short, skimmable follow-up email
- voicemailScript: 15–20s voicemail script

STRICT requirements:
- DO NOT mention channels outside Spectrum Reach solutions (no radio, print, billboards, social-only, etc.).
- Use local phrasing (e.g., "in your area", reference neighborhood dynamics when relevant).
- Avoid generic filler (e.g., "increase brand awareness"). Make each bullet concrete and sales-usable.
- If Category is missing or unclear, respond but keep bullets framed as questions to confirm fit.
`;

      // Proxy controlled strictly by flag; do not override based on key presence
      const useProxy = (process.env.REACT_APP_USE_OPENAI_PROXY || '').toLowerCase() === 'true';
      const allowDirectFallback = (!!process.env.REACT_APP_OPENAI_API_KEY) && ((process.env.REACT_APP_ALLOW_DIRECT_FALLBACK || '').toLowerCase() === 'true');
      let content = '';
      const isLocal = (typeof window !== 'undefined') && (window.location.hostname === 'localhost');
      const clientForRepair = ensureOpenAI();
      const toText = (value: any): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (Array.isArray(value)) {
          return value.map(v => toText(v)).filter(Boolean).join(' | ');
        }
        if (typeof value === 'object') {
          if (typeof value.text === 'string') return value.text;
          if (typeof value.why === 'string') return value.why;
          if (typeof value.question === 'string') return value.question;
          if (typeof value.response === 'string') return value.response;
          try {
            return JSON.stringify(value);
          } catch {
            return String(value);
          }
        }
        return String(value);
      };
      const toStringArray = (value: any): string[] => {
        if (!Array.isArray(value)) return [];
        return value
          .map((v: any) => toText(v).trim())
          .filter(Boolean);
      };
      const normalizeMediaMix = (value: any): Array<{ channel: string; percent?: number; why: string }> | undefined => {
        if (!Array.isArray(value)) return undefined;
        const allowed = new Set(['Linear TV', 'Streaming TV', 'CTV', 'Online Video', 'Display', 'Search Engine Marketing', 'SEM']);
        return value
          .map((entry: any) => {
            if (typeof entry === 'string') {
              return { channel: entry, why: '' };
            }
            const percent = typeof entry?.percent === 'number'
              ? entry.percent
              : Number.isFinite(Number(entry?.percent))
                ? Number(entry.percent)
                : undefined;
            return {
              channel: toText(entry?.channel),
              percent,
              why: toText(entry?.why)
            };
          })
          .filter((entry) => allowed.has((entry.channel || '').trim()))
          .map((entry) => ({
            channel: entry.channel === 'SEM' ? 'Search Engine Marketing' : entry.channel,
            percent: entry.percent,
            why: entry.why
          }));
      };

      // Prefer function-calling to force valid JSON
      const strategyTool: any = {
        type: 'function',
        function: {
          name: 'return_strategy',
          description: 'Return the appointment strategy strictly as JSON fields',
          parameters: {
            type: 'object',
            properties: {
              openingLine: { type: 'string' },
              bestTimeToCall: { type: 'string' },
              keyTalkingPoints: { type: 'array', items: { type: 'string' } },
              objectionHandlers: { type: 'array', items: { type: 'string' } },
              valueProposition: { type: 'string' },
              callToAction: { type: 'string' },
              competitiveAngles: { type: 'array', items: { type: 'string' } },
              verticalInsights: { type: 'array', items: { type: 'string' } },
              discoveryQuestions: { type: 'array', items: { type: 'string' } },
              hookLines: { type: 'array', items: { type: 'string' } },
              mediaMix: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    channel: { type: 'string' },
                    percent: { type: 'number' },
                    why: { type: 'string' }
                  },
                  required: ['channel']
                }
              },
              plan30Day: { type: 'array', items: { type: 'string' } },
              introEmail: { type: 'string' },
              followUpEmail: { type: 'string' },
              voicemailScript: { type: 'string' }
            },
            required: ['openingLine','bestTimeToCall','keyTalkingPoints','objectionHandlers','valueProposition','callToAction']
          }
        }
      };

      const useBackground = (process.env.REACT_APP_USE_BACKGROUND_AI || '').toLowerCase() === 'true';

      if (useBackground) {
        // Background job flow
        const jobId = `job_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
        try {
          // Queue job
          await fetch('/.netlify/functions/ai-strategy-background', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ jobId, prospect })
          });
          // Poll for completion with backoff
          const start = Date.now();
          const timeoutMs = 180000; // 3 minutes to avoid premature busy errors
          let delay = 800;
          const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
          let consecutiveErrors = 0;
          while (Date.now() - start < timeoutMs) {
            // Use long-poll wait endpoint to minimize Firestore reads
            const resp = await fetch(`/.netlify/functions/ai-strategy-status?jobId=${encodeURIComponent(jobId)}`);
            if (!resp.ok) {
              // If server error persists, fallback to direct path
              consecutiveErrors++;
              if (resp.status === 429) {
                const backoff = Number((await resp.json().catch(()=>({})))?.retryAfterMs) || delay;
                await sleep(backoff);
                delay = Math.min(2500, Math.floor((backoff || delay) * 1.5));
                continue;
              }
              if (resp.status === 202) {
                const backoff = Number((await resp.json().catch(()=>({})))?.retryAfterMs) || delay;
                await sleep(backoff);
                delay = Math.min(2500, Math.floor((backoff || delay) * 1.3));
                continue;
              }
              if (resp.status >= 500 && consecutiveErrors >= 2) {
                console.warn('[AI] Background status 5xx; falling back to direct path');
                break;
              }
              await sleep(delay);
              delay = Math.min(2500, Math.floor(delay * 1.5));
              continue;
            }
            const data = await resp.json().catch(() => null);
            const status = data?.status || (data?.job ? data.job.status : 'pending');
            const job = data?.job;
            if (status === 'complete' && job?.strategy) {
              return {
                openingLine: toText(job.strategy.openingLine) || `Hi, I'm calling about local advertising opportunities for ${prospect.name}.`,
                bestTimeToCall: toText(job.strategy.bestTimeToCall) || 'Tuesday-Thursday, 9-11 AM or 2-4 PM',
                keyTalkingPoints: toStringArray(job.strategy.keyTalkingPoints),
                objectionHandlers: toStringArray(job.strategy.objectionHandlers),
                valueProposition: toText(job.strategy.valueProposition) || 'Help you reach more local customers and grow your business',
                callToAction: toText(job.strategy.callToAction) || 'Would you be available for a 15-minute meeting this week?',
                competitiveAngles: toStringArray(job.strategy.competitiveAngles),
                verticalInsights: toStringArray(job.strategy.verticalInsights),
                discoveryQuestions: toStringArray(job.strategy.discoveryQuestions),
                hookLines: toStringArray(job.strategy.hookLines),
                mediaMix: normalizeMediaMix(job.strategy.mediaMix),
                plan30Day: toStringArray(job.strategy.plan30Day || job.strategy['30DayPlan']),
                introEmail: toText(job.strategy.introEmail),
                followUpEmail: toText(job.strategy.followUpEmail),
                voicemailScript: toText(job.strategy.voicemailScript)
              };
            }
            // Back off and continue when pending
            const retryMs = Number(data?.retryAfterMs || 0) || delay;
            if (status === 'pending') {
              const waitMs = retryMs > 0 ? retryMs : delay;
              await sleep(waitMs);
              delay = Math.min(2500, Math.floor(waitMs * 1.3));
              continue;
            }
            if (status === 'error') {
              throw new Error('AI job failed on the server. Please try again.');
            }
            await sleep(delay);
            delay = Math.min(2500, Math.floor(delay * 1.5));
          }
          // If we exit without result, surface friendly message (no direct fallback)
          throw new Error('AI service is busy. Please try again in a moment.');
        } catch (e) {
          throw e;
        }
      }

      if (useProxy) {
        // Helper: POST with timeout, safe parse, and one retry on 5xx/timeout
        const postViaProxy = async (url: string): Promise<string> => {
          const attempt = async (): Promise<{ ok: boolean; status: number; content: string; error?: string }> => {
            const controller = new AbortController();
            const t = setTimeout(() => controller.abort(), 30000); // 30s client timeout
            try {
              const t0 = Date.now();
              const resp = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt }),
                signal: controller.signal
              });
              clearTimeout(t);
              const elapsed = Date.now() - t0;
              let raw = '';
              try { raw = await resp.text(); } catch { raw = ''; }
              let data: any = null;
              try { data = raw ? JSON.parse(raw) : null; } catch { /* ignore parse errors */ }
              const ok = resp.ok;
              console.log(`[AI] Proxy attempt status=${resp.status} elapsedMs=${elapsed} ok=${ok}`);
              return { ok, status: resp.status, content: data?.content || '', error: data?.error || raw };
            } catch (e: any) {
              clearTimeout(t);
              console.warn('[AI] Proxy attempt network error:', e?.message || e);
              return { ok: false, status: 0, content: '', error: e?.message || 'Network error' };
            }
          };
          // First try
          let result = await attempt();
          // Retry once on gateway/timeouts or generic 5xx
          if (!result.ok && (result.status === 504 || result.status === 502 || result.status === 500 || result.status === 0)) {
            await new Promise(r => setTimeout(r, 800));
            result = await attempt();
          }
          if (!result.ok) {
            throw new Error(result.error || `Proxy error (HTTP ${result.status})`);
          }
          if (!result.content) {
            throw new Error('Empty content from proxy');
          }
          return result.content;
        };

        if (!isLocal) {
          // Production: use Netlify function
          try {
            content = await postViaProxy('/.netlify/functions/openai-proxy');
          } catch (proxyErr) {
            if (allowDirectFallback) {
              console.warn('[AI] Proxy failed in production, using direct fallback:', (proxyErr as any)?.message || proxyErr);
              const client = ensureOpenAI();
              if (!client) throw new Error('OpenAI client key missing and proxy unavailable');
              const t0 = Date.now();
              const direct = await client.chat.completions.create({
                model: (process.env.REACT_APP_OPENAI_MODEL || "gpt-4-turbo"),
                messages: [
                  { role: "system", content: "You are an expert local advertising sales consultant. Use the provided function to return ALL fields. If a field isn't applicable, return an empty string or empty array. No omissions. Minimums: keyTalkingPoints≥8, objectionHandlers≥8, competitiveAngles≥6, verticalInsights≥6, discoveryQuestions≥8, hookLines≥6, mediaMix≥4, plan30Day≥8." },
                  { role: "user", content: prompt }
                ],
                temperature: 0.1,
                max_tokens: 1600,
                tools: [strategyTool],
                tool_choice: { type: 'function', function: { name: 'return_strategy' } } as any
              });
              console.log(`[AI] Direct OpenAI (prod fallback) elapsedMs=${Date.now() - t0}`);
              const choice: any = direct.choices?.[0];
              const toolArgs = choice?.message?.tool_calls?.[0]?.function?.arguments;
              content = toolArgs || choice?.message?.content || '';
            } else {
              throw proxyErr;
            }
          }
        } else {
          // Local: try Netlify dev function first, then fall back to direct
          try {
            content = await postViaProxy('http://localhost:8888/.netlify/functions/openai-proxy');
          } catch (proxyErr) {
            console.warn('[AI] Proxy failed, falling back to direct OpenAI call:', (proxyErr as any)?.message || proxyErr);
            const client = ensureOpenAI();
            if (!client) throw new Error('OpenAI client key missing and proxy unavailable');
            const t0 = Date.now();
            const direct = await client.chat.completions.create({
              model: (process.env.REACT_APP_OPENAI_MODEL || "gpt-4-turbo"),
              messages: [
                { role: "system", content: "You are an expert local advertising sales consultant. Use the provided function to return ALL fields. If a field isn't applicable, return an empty string or empty array. No omissions. Minimums: keyTalkingPoints≥8, objectionHandlers≥8, competitiveAngles≥6, verticalInsights≥6, discoveryQuestions≥8, hookLines≥6, mediaMix≥4, plan30Day≥8." },
                { role: "user", content: prompt }
              ],
              temperature: 0.1,
              max_tokens: 1600,
              tools: [strategyTool],
              tool_choice: { type: 'function', function: { name: 'return_strategy' } } as any
            });
            console.log(`[AI] Direct OpenAI elapsedMs=${Date.now() - t0}`);
            const choice: any = direct.choices?.[0];
            const toolArgs = choice?.message?.tool_calls?.[0]?.function?.arguments;
            content = toolArgs || choice?.message?.content || '';
          }
        }
      } else {
        // Direct browser call (no proxy)
        const client = ensureOpenAI();
        if (!client) throw new Error('OpenAI browser key missing. Enable proxy or provide key.');
        const t0 = Date.now();
        const direct = await client.chat.completions.create({
          model: (process.env.REACT_APP_OPENAI_MODEL || "gpt-4-turbo"),
          messages: [
            { role: "system", content: "You are an expert local advertising sales consultant. Use the provided function to return ALL fields. If a field isn't applicable, return an empty string or empty array. No omissions. Minimums: keyTalkingPoints≥8, objectionHandlers≥8, competitiveAngles≥6, verticalInsights≥6, discoveryQuestions≥8, hookLines≥6, mediaMix≥4, plan30Day≥8." },
            { role: "user", content: prompt }
          ],
          temperature: 0.1,
          max_tokens: 1600,
          tools: [strategyTool],
          tool_choice: { type: 'function', function: { name: 'return_strategy' } } as any
        });
        console.log(`[AI] Direct OpenAI elapsedMs=${Date.now() - t0}`);
        const choice: any = direct.choices?.[0];
        const toolArgs = choice?.message?.tool_calls?.[0]?.function?.arguments;
        content = toolArgs || choice?.message?.content || '';
      }
      
      // Try to parse JSON response (robust): extract balanced JSON, normalize quotes, strip trailing commas
      const tryParseStrategy = (raw: string) => {
        const stripFence = (s: string) => {
          const m = s.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
          return m ? m[1] : s;
        };
        const extractBalancedJson = (s: string): string | null => {
          const text = stripFence(s).trim();
          const start = text.indexOf('{');
          if (start < 0) return null;
          let depth = 0;
          let inStr = false;
          let esc = false;
          for (let i = start; i < text.length; i++) {
            const ch = text[i];
            if (inStr) {
              if (esc) { esc = false; }
              else if (ch === '\\') { esc = true; }
              else if (ch === '"') { inStr = false; }
            } else {
              if (ch === '"') inStr = true;
              else if (ch === '{') depth++;
              else if (ch === '}') {
                depth--;
                if (depth === 0) {
                  return text.slice(start, i + 1);
                }
              }
            }
          }
          return null;
        };
        const candidate = extractBalancedJson(raw) || stripFence(raw);
        // Normalize smart quotes and remove trailing commas outside of strings
        let sanitized = candidate
          .replace(/[\u201C\u201D]/g, '"')
          .replace(/[\u2018\u2019]/g, "'");
        // Trailing commas: ", ]" or ", }" (only ']' needs escaping in a char class)
        sanitized = sanitized.replace(/,(\s*[}\]])/g, '$1');
        return JSON.parse(sanitized);
      };

      // Helper: attempt model-based JSON repair if parsing fails
      const repairJsonIfNeeded = async (raw: string): Promise<string | null> => {
        try {
          const repairPrompt = `You will be given text that should be a single JSON object for an appointment strategy. Return ONLY valid JSON (no markdown, no commentary). If fields are missing, keep them as empty strings or empty arrays. Text to fix:\n\n${raw}`;
          if (useProxy) {
            const url = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
              ? 'http://localhost:8888/.netlify/functions/openai-proxy'
              : '/.netlify/functions/openai-proxy';
            const resp = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ prompt: repairPrompt })
            });
            const data = await resp.json().catch(() => null);
            if (resp.ok && data?.content) return data.content as string;
            return null;
          } else {
            const client = ensureOpenAI();
            if (!client) return null;
            const fixed = await client.chat.completions.create({
              model: (process.env.REACT_APP_OPENAI_MODEL || "gpt-4-turbo"),
              messages: [
                { role: 'system', content: 'You repair and output ONLY valid JSON.' },
                { role: 'user', content: repairPrompt }
              ],
              temperature: 0,
              max_tokens: 800,
              response_format: { type: 'json_object' } as any
            });
            return fixed.choices?.[0]?.message?.content || null;
          }
        } catch {
          return null;
        }
      };

      try {
        let strategy = tryParseStrategy(content);
        // Fill missing fields pass
        const requiredArrays = ["keyTalkingPoints","objectionHandlers","competitiveAngles","verticalInsights","discoveryQuestions","hookLines","mediaMix","plan30Day"];
        const scalarFields = ["openingLine","bestTimeToCall","valueProposition","callToAction","introEmail","followUpEmail","voicemailScript"];
        const missingArrays = requiredArrays.filter(k => !Array.isArray(strategy?.[k]) || strategy[k].length === 0);
        const missingScalars = scalarFields.filter(k => !strategy?.[k] || String(strategy[k]).trim() === "");
        if ((missingArrays.length || missingScalars.length) && clientForRepair) {
          const repairTool: any = { type: 'function', function: { name: 'return_strategy', description: 'Return ONLY the missing fields as valid JSON.', parameters: { type: 'object', properties: Object.fromEntries(
            [...missingArrays, ...missingScalars].map(k => [k, requiredArrays.includes(k) ? (k==='mediaMix' ? { type:'array', items:{type:'object'} } : { type:'array', items:{type:'string'} }) : { type:'string' }]) ) } } };
          const fixed = await clientForRepair.chat.completions.create({
            model: (process.env.REACT_APP_OPENAI_MODEL || "gpt-4-turbo"),
            messages: [
              { role: 'system', content: 'Return ONLY the missing fields via the function. Keep outputs concise and category‑specific.' },
              { role: 'user', content: `Business: ${prospect.name}\nCategory: ${prospect.category}\nMissing: ${[...missingArrays, ...missingScalars].join(', ')}` }
            ],
            temperature: 0.1,
            max_tokens: 600,
            tools: [repairTool],
            tool_choice: { type: 'function', function: { name: 'return_strategy' } } as any
          });
          const choice: any = fixed.choices?.[0];
          const args = choice?.message?.tool_calls?.[0]?.function?.arguments || '{}';
          const patch = JSON.parse(args);
          strategy = { ...strategy, ...patch };
        }
          // Merge enriched fields into existing ones without breaking UI
          // Normalize talking points to strings only (do NOT include mediaMix objects)
          const tpSource: any[] = [
            ...(Array.isArray(strategy.keyTalkingPoints) ? strategy.keyTalkingPoints : []),
            ...(Array.isArray(strategy.verticalInsights) ? strategy.verticalInsights : []),
            ...(Array.isArray(strategy.competitiveAngles) ? strategy.competitiveAngles : [])
          ];
          const mergedTalkingPoints = tpSource
            .map((v) => typeof v === 'string' ? v : (v?.text || v?.tip || v?.why || JSON.stringify(v)))
            .filter(Boolean)
            .slice(0, 12);

          // Normalize objections: accept strings or objects {objection, response}
          const objSource: any[] = [
            ...(Array.isArray(strategy.objectionHandlers) ? strategy.objectionHandlers : []),
            ...(Array.isArray(strategy.discoveryQuestions) ? strategy.discoveryQuestions.map((q: any) => typeof q === 'string' ? `If asked: ${q}` : `If asked: ${q?.question || JSON.stringify(q)}`) : [])
          ];
          const mergedObjections = objSource
            .map((v: any) => {
              if (typeof v === 'string') return v;
              if (v && (v.objection || v.response)) {
                const o = typeof v.objection === 'string' ? v.objection : JSON.stringify(v.objection);
                const r = typeof v.response === 'string' ? v.response : JSON.stringify(v.response);
                return `${o} → ${r}`;
              }
              return JSON.stringify(v);
            })
            .filter(Boolean)
            .slice(0, 12);
          // Normalize mediaMix to SR channels only
          let mediaMix: Array<{ channel: string; percent?: number; why: string }> | undefined;
          if (Array.isArray(strategy.mediaMix)) {
            const normalizeEntry = (e: any) => {
              if (typeof e === 'string') {
                return { channel: e, why: '' };
              }
              return { channel: e.channel, percent: e.percent, why: e.why };
            };
            const allowed = new Set(['Linear TV', 'Streaming TV', 'CTV', 'Online Video', 'Display', 'Search Engine Marketing', 'SEM']);
            mediaMix = (strategy.mediaMix || []).map(normalizeEntry)
              .filter((e: any) => allowed.has((e.channel || '').trim()))
              .map((e: any) => ({ channel: e.channel === 'SEM' ? 'Search Engine Marketing' : e.channel, percent: e.percent, why: e.why }));
          }

          return {
            openingLine: strategy.openingLine || `Hi, I'm calling about local advertising opportunities for ${prospect.name}.`,
            bestTimeToCall: strategy.bestTimeToCall || "Tuesday-Thursday, 9-11 AM or 2-4 PM",
            keyTalkingPoints: mergedTalkingPoints.length ? mergedTalkingPoints : ["Local advertising opportunities", "Proven results for similar businesses"],
            objectionHandlers: mergedObjections.length ? mergedObjections : ["We're not interested right now", "We don't have the budget"],
            valueProposition: strategy.valueProposition || "Help you reach more local customers and grow your business",
            callToAction: strategy.callToAction || strategy.nextStep || "Would you be available for a 15-minute meeting this week?",
            competitiveAngles: toStringArray(strategy.competitiveAngles),
            verticalInsights: toStringArray(strategy.verticalInsights),
            discoveryQuestions: toStringArray(strategy.discoveryQuestions),
            hookLines: toStringArray(strategy.hookLines),
            mediaMix,
            plan30Day: toStringArray(strategy['30DayPlan'] || strategy.plan30Day),
            introEmail: toText(strategy.introEmail),
            followUpEmail: toText(strategy.followUpEmail),
            voicemailScript: toText(strategy.voicemailScript)
          };
      } catch (error) {
        console.warn('Failed to parse appointment strategy JSON:', error);
        // Attempt one repair pass
        try {
          const repaired = await repairJsonIfNeeded(content);
          if (repaired) {
            const strategy = tryParseStrategy(repaired);
            const tpSource: any[] = [
              ...(Array.isArray(strategy.keyTalkingPoints) ? strategy.keyTalkingPoints : []),
              ...(Array.isArray(strategy.verticalInsights) ? strategy.verticalInsights : []),
              ...(Array.isArray(strategy.competitiveAngles) ? strategy.competitiveAngles : [])
            ];
            const mergedTalkingPoints = tpSource
              .map((v) => typeof v === 'string' ? v : (v?.text || v?.tip || v?.why || JSON.stringify(v)))
              .filter(Boolean)
              .slice(0, 12);
            const objSource: any[] = [
              ...(Array.isArray(strategy.objectionHandlers) ? strategy.objectionHandlers : []),
              ...(Array.isArray(strategy.discoveryQuestions) ? strategy.discoveryQuestions.map((q: any) => typeof q === 'string' ? `If asked: ${q}` : `If asked: ${q?.question || JSON.stringify(q)}`) : [])
            ];
            const mergedObjections = objSource
              .map((v: any) => {
                if (typeof v === 'string') return v;
                if (v && (v.objection || v.response)) {
                  const o = typeof v.objection === 'string' ? v.objection : JSON.stringify(v.objection);
                  const r = typeof v.response === 'string' ? v.response : JSON.stringify(v.response);
                  return `${o} → ${r}`;
                }
                return JSON.stringify(v);
              })
              .filter(Boolean)
              .slice(0, 12);
            let mediaMix: Array<{ channel: string; percent?: number; why: string }> | undefined;
            if (Array.isArray(strategy.mediaMix)) {
              const normalizeEntry = (e: any) => {
                if (typeof e === 'string') {
                  return { channel: e, why: '' };
                }
                return { channel: e.channel, percent: e.percent, why: e.why };
              };
              const allowed = new Set(['Linear TV', 'Streaming TV', 'CTV', 'Online Video', 'Display', 'Search Engine Marketing', 'SEM']);
              mediaMix = (strategy.mediaMix || []).map(normalizeEntry)
                .filter((e: any) => allowed.has((e.channel || '').trim()))
                .map((e: any) => ({ channel: e.channel === 'SEM' ? 'Search Engine Marketing' : e.channel, percent: e.percent, why: e.why }));
            }
            return {
              openingLine: strategy.openingLine || `Hi, I'm calling about local advertising opportunities for ${prospect.name}.`,
              bestTimeToCall: strategy.bestTimeToCall || "Tuesday-Thursday, 9-11 AM or 2-4 PM",
              keyTalkingPoints: mergedTalkingPoints.length ? mergedTalkingPoints : ["Local advertising opportunities", "Proven results for similar businesses"],
              objectionHandlers: mergedObjections.length ? mergedObjections : ["We're not interested right now", "We don't have the budget"],
              valueProposition: strategy.valueProposition || "Help you reach more local customers and grow your business",
              callToAction: strategy.callToAction || strategy.nextStep || "Would you be available for a 15-minute meeting this week?",
              competitiveAngles: toStringArray(strategy.competitiveAngles),
              verticalInsights: toStringArray(strategy.verticalInsights),
              discoveryQuestions: toStringArray(strategy.discoveryQuestions),
              hookLines: toStringArray(strategy.hookLines),
              mediaMix,
              plan30Day: toStringArray(strategy['30DayPlan'] || strategy.plan30Day),
              introEmail: toText(strategy.introEmail),
              followUpEmail: toText(strategy.followUpEmail),
              voicemailScript: toText(strategy.voicemailScript)
            };
          }
        } catch (repairErr) {
          console.warn('JSON repair attempt failed:', repairErr);
        }
      }

      // Fallback strategy
      return {
        openingLine: `Hi, I'm calling about local advertising opportunities for ${prospect.name}.`,
        bestTimeToCall: "Tuesday-Thursday, 9-11 AM or 2-4 PM",
        keyTalkingPoints: [
          "Local advertising opportunities in your area",
          "Proven results for similar businesses",
          "Flexible advertising solutions"
        ],
        objectionHandlers: [
          "We're not interested right now → 'I understand. When would be a better time to discuss this?'",
          "We don't have the budget → 'What if I could show you how this pays for itself?'"
        ],
        valueProposition: "Help you reach more local customers and grow your business",
        callToAction: "Would you be available for a 15-minute meeting this week?"
      };
    } catch (error) {
      console.error('Failed to generate appointment strategy:', error);
      
      // Return basic fallback strategy
      return {
        openingLine: `Hi, I'm calling about local advertising opportunities for ${prospect.name}.`,
        bestTimeToCall: "Tuesday-Thursday, 9-11 AM or 2-4 PM",
        keyTalkingPoints: [
          "Local advertising opportunities in your area",
          "Proven results for similar businesses",
          "Flexible advertising solutions"
        ],
        objectionHandlers: [
          "We're not interested right now → 'I understand. When would be a better time to discuss this?'",
          "We don't have the budget → 'What if I could show you how this pays for itself?'"
        ],
        valueProposition: "Help you reach more local customers and grow your business",
        callToAction: "Would you be available for a 15-minute meeting this week?"
      };
    }
  }

  private async runBackgroundStrategyJob(
    prospect: ProspectData,
    options?: { orgKey?: OrganizationKey; liteScanContext?: string }
  ): Promise<Record<string, any>> {
    const jobId = `job_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const queueResp = await fetch('/.netlify/functions/ai-strategy-background', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jobId,
        prospect,
        orgKey: options?.orgKey || DEFAULT_ORGANIZATION,
        liteScanContext: options?.liteScanContext || ''
      })
    });
    if (!queueResp.ok) {
      throw new Error(`Failed to queue strategy job (HTTP ${queueResp.status})`);
    }

    const start = Date.now();
    const timeoutMs = 180000;
    let delay = 800;
    const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

    while (Date.now() - start < timeoutMs) {
      const statusResp = await fetch(
        `/.netlify/functions/ai-strategy-status?jobId=${encodeURIComponent(jobId)}`
      );
      if (!statusResp.ok) {
        if (statusResp.status === 429 || statusResp.status === 202) {
          const backoff = Number((await statusResp.json().catch(() => ({})))?.retryAfterMs) || delay;
          await sleep(backoff);
          delay = Math.min(2500, Math.floor((backoff || delay) * 1.5));
          continue;
        }
        await sleep(delay);
        delay = Math.min(2500, Math.floor(delay * 1.5));
        continue;
      }

      const data = await statusResp.json().catch(() => null);
      const status = data?.status || data?.job?.status || 'pending';
      const job = data?.job;

      if (status === 'complete' && job?.strategy) {
        return job.strategy as Record<string, any>;
      }
      if (status === 'error') {
        throw new Error(job?.error || 'AI strategy job failed on the server');
      }

      const retryMs = Number(data?.retryAfterMs || 0) || delay;
      await sleep(retryMs > 0 ? retryMs : delay);
      delay = Math.min(2500, Math.floor((retryMs || delay) * 1.3));
    }

    throw new Error('AI service is busy. Please try again in a moment.');
  }

  private mapGemmetricStrategyFromParsed(
    parsed: Record<string, any>,
    prospect: ProspectData,
    liteScan?: GemmetricLiteScanResult | null
  ) {
    const fallback = this.getGemmetricStrategyFallback(prospect, liteScan);
    const toText = (value: any): string => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      if (Array.isArray(value)) return value.map((v) => toText(v)).filter(Boolean).join(' | ');
      if (typeof value === 'object') {
        if (typeof value.text === 'string') return value.text;
        if (typeof value.why === 'string') return value.why;
        if (typeof value.question === 'string') return value.question;
        if (typeof value.response === 'string') return value.response;
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    };
    const toStringArray = (value: any): string[] => {
      if (!Array.isArray(value)) return [];
      return value.map((v: any) => toText(v).trim()).filter(Boolean);
    };

    return {
      openingLine: toText(parsed.openingLine) || fallback.openingLine,
      bestTimeToCall: toText(parsed.bestTimeToCall) || fallback.bestTimeToCall,
      keyTalkingPoints: toStringArray(parsed.keyTalkingPoints).length
        ? toStringArray(parsed.keyTalkingPoints)
        : fallback.keyTalkingPoints,
      objectionHandlers: toStringArray(parsed.objectionHandlers).length
        ? toStringArray(parsed.objectionHandlers)
        : fallback.objectionHandlers,
      valueProposition: toText(parsed.valueProposition) || fallback.valueProposition,
      callToAction: toText(parsed.callToAction) || fallback.callToAction,
      competitiveAngles: toStringArray(parsed.competitiveAngles),
      verticalInsights: toStringArray(parsed.verticalInsights),
      discoveryQuestions: toStringArray(parsed.discoveryQuestions).length
        ? toStringArray(parsed.discoveryQuestions)
        : fallback.discoveryQuestions,
      hookLines: toStringArray(parsed.hookLines).length ? toStringArray(parsed.hookLines) : fallback.hookLines,
      visibilityPriorities: toStringArray(parsed.visibilityPriorities).length
        ? toStringArray(parsed.visibilityPriorities)
        : fallback.visibilityPriorities,
      liteScanInsights: toStringArray(parsed.liteScanInsights).length
        ? toStringArray(parsed.liteScanInsights)
        : fallback.liteScanInsights,
      plan30Day: toStringArray(parsed.plan30Day || parsed['30DayPlan']),
      introEmail: toText(parsed.introEmail),
      followUpEmail: toText(parsed.followUpEmail),
      voicemailScript: toText(parsed.voicemailScript)
    };
  }

  private buildGemmetricLiteScanContext(liteScan?: GemmetricLiteScanResult | null): string {
    if (!liteScan) {
      return 'Initial assessment: Not run yet. Base recommendations on category fit and public metadata only. Do not claim confirmed technical issues.';
    }

    const findings = (liteScan.findings || [])
      .map((f) => {
        const label = f.businessLabel || f.label;
        const impact = f.whyItMatters ? ` — ${f.whyItMatters}` : '';
        return `${label}: ${f.status}${impact}${f.detail ? ` (${f.detail})` : ''}`;
      })
      .join('\n');
    const strengths = (liteScan.snapshot?.strengths || []).join('\n');
    const opportunities = (liteScan.snapshot?.opportunities || liteScan.observations || []).join('\n');

    return [
      `Initial assessment summary: ${liteScan.summary || 'Completed'}`,
      liteScan.opportunityScore !== undefined ? `Opportunity score: ${liteScan.opportunityScore}` : '',
      liteScan.opportunityLevel
        ? `Opportunity level: ${liteScan.opportunityLevel}`
        : liteScan.visibilityRisk
          ? `Opportunity level (legacy): ${liteScan.visibilityRisk}`
          : '',
      strengths ? `Strengths:\n${strengths}` : '',
      opportunities ? `Potential opportunities:\n${opportunities}` : '',
      findings ? `Technical indicators:\n${findings}` : ''
    ].filter(Boolean).join('\n');
  }

  private getGemmetricStrategyFallback(
    prospect: ProspectData,
    liteScan?: GemmetricLiteScanResult | null
  ) {
    const liteContext = liteScan?.summary ? ` Based on Lite Scan: ${liteScan.summary}` : '';
    return {
      openingLine: `Hi, I'm reaching out about how ${prospect.name} appears in AI-powered search and answer engines.${liteContext}`,
      bestTimeToCall: 'Tuesday-Thursday, 9-11 AM or 2-4 PM',
      keyTalkingPoints: [
        'AI assistants increasingly shape how local customers discover businesses',
        'Entity clarity across your website helps machines understand who you are',
        'Structured data and metadata improve AI retrieval confidence',
        'Crawlability issues can hide strong businesses from AI-generated answers',
        'Competitors may already be optimizing for AI visibility in your category',
        'A focused AI visibility review can surface quick technical wins'
      ],
      objectionHandlers: [
        "We already rank on Google → 'AI answers use different signals than classic SEO rankings.'",
        "We don't have budget → 'A Lite Scan often reveals fixes that don't require a full rebuild.'",
        "Our website is fine → 'Even strong sites can be hard for AI systems to interpret without schema and entity clarity.'",
        "Send me info → 'Happy to — can we book 15 minutes to walk through what AI systems likely see today?'"
      ],
      valueProposition: `Help ${prospect.name} become easier for AI systems to understand, cite, and recommend in local discovery moments.`,
      callToAction: 'Would you be open to a short AI visibility review this week?',
      visibilityPriorities: [
        'Confirm Organization/LocalBusiness schema coverage',
        'Strengthen title and meta description entity signals',
        'Check robots.txt and crawl paths for unintended blocking',
        'Benchmark AI answer visibility against local competitors'
      ],
      liteScanInsights: liteScan?.observations?.length ? liteScan.observations.slice(0, 6) : undefined,
      discoveryQuestions: [
        'How are new customers finding you today beyond referrals?',
        'Do you know how your business appears in AI chat or voice answers?',
        'Who owns website and SEO decisions internally?',
        'Have you added or updated structured data recently?'
      ],
      hookLines: [
        'Quick idea to improve how AI systems describe your business locally.',
        'Noticed a few signals that may affect AI discoverability — can I share?'
      ]
    };
  }

  async generateGemmetricAppointmentStrategy(
    prospect: ProspectData,
    liteScan?: GemmetricLiteScanResult | null
  ): Promise<{
    openingLine: string;
    bestTimeToCall: string;
    keyTalkingPoints: string[];
    objectionHandlers: string[];
    valueProposition: string;
    callToAction: string;
    competitiveAngles?: string[];
    verticalInsights?: string[];
    discoveryQuestions?: string[];
    hookLines?: string[];
    visibilityPriorities?: string[];
    liteScanInsights?: string[];
    plan30Day?: string[];
    introEmail?: string;
    followUpEmail?: string;
    voicemailScript?: string;
  }> {
    const liteScanContext = this.buildGemmetricLiteScanContext(liteScan);
    const prompt = `
You are an expert AI VISIBILITY / AI DISCOVERABILITY consultant for Gemmetric. Produce a seller-ready appointment strategy focused on AI Visibility Infrastructure — not paid media or ad channels.

Business: ${prospect.name}
Category: ${prospect.category}
Rating: ${prospect.rating || 'N/A'} (${prospect.reviewCount || 0} reviews)
Address: ${prospect.address}
Website Present: ${prospect.website ? 'Yes' : 'No'}

${liteScanContext}

Return ONLY valid JSON with this shape (all base fields required):
{
  "openingLine": string,
  "bestTimeToCall": string,
  "keyTalkingPoints": string[],
  "objectionHandlers": string[],
  "valueProposition": string,
  "callToAction": string
}

Add optional enrichment fields (category-specific, 4-8 items each):
- competitiveAngles: AI visibility positioning vs local competitors
- verticalInsights: category realities for AI search/discoverability
- discoveryQuestions: qualifiers about website, schema, entity clarity, crawlability
- hookLines: short voicemail/walk-in hooks about AI visibility
- visibilityPriorities: ordered technical/discoverability priorities (schema, entity clarity, crawlability, metadata, AI answer visibility)
- liteScanInsights: observations grounded in Lite Scan (if provided); otherwise likely opportunity hypotheses
- plan30Day: first 30-day AI visibility action plan
- introEmail, followUpEmail, voicemailScript

STRICT requirements:
- DO NOT mention Spectrum Reach, Linear TV, CTV, display ads, SEM, or any paid media mix.
- Frame findings as observations or likely opportunities — not a full audit unless Lite Scan data is present.
- Focus on crawlability, entity clarity, structured data, metadata, and AI answer engine discoverability.
`;

    try {
      const useBackground = (process.env.REACT_APP_USE_BACKGROUND_AI || '').toLowerCase() === 'true';
      const useProxy = (process.env.REACT_APP_USE_OPENAI_PROXY || '').toLowerCase() === 'true';
      const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';

      if (useBackground) {
        console.log(`🎯 Generating Gemmetric strategy via background job for ${prospect.name}...`);
        const strategy = await this.runBackgroundStrategyJob(prospect, {
          orgKey: 'gemmetric',
          liteScanContext
        });
        return this.mapGemmetricStrategyFromParsed(strategy, prospect, liteScan);
      }

      let content = '';
      const postViaProxy = async (url: string): Promise<string> => {
        const attempt = async () => {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 30000);
          try {
            const resp = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ prompt }),
              signal: controller.signal
            });
            clearTimeout(timer);
            const data = await resp.json().catch(() => ({}));
            return { ok: resp.ok, status: resp.status, content: data?.content || '', error: data?.error };
          } catch (error: any) {
            clearTimeout(timer);
            return { ok: false, status: 0, content: '', error: error?.message || 'Network error' };
          }
        };

        let result = await attempt();
        if (!result.ok && (result.status === 504 || result.status === 502 || result.status === 500 || result.status === 0)) {
          await new Promise((resolve) => setTimeout(resolve, 800));
          result = await attempt();
        }
        if (!result.ok || !result.content) {
          throw new Error(result.error || `Gemmetric strategy proxy failed (HTTP ${result.status})`);
        }
        return result.content;
      };

      if (useProxy) {
        const url = isLocal
          ? 'http://localhost:8888/.netlify/functions/openai-proxy'
          : '/.netlify/functions/openai-proxy';
        content = await postViaProxy(url);
      } else {
        const client = ensureOpenAI();
        if (!client) {
          throw new Error('OpenAI unavailable for Gemmetric strategy. Enable background AI or proxy.');
        }
        const direct = await client.chat.completions.create({
          model: process.env.REACT_APP_OPENAI_MODEL || 'gpt-4-turbo',
          messages: [
            { role: 'system', content: 'Return ONLY valid JSON for a Gemmetric AI visibility appointment strategy. Never include paid media channels.' },
            { role: 'user', content: prompt }
          ],
          temperature: 0.1,
          max_tokens: 1600,
          response_format: { type: 'json_object' } as any
        });
        content = direct.choices?.[0]?.message?.content || '';
      }

      const parsed = JSON.parse(content);
      return this.mapGemmetricStrategyFromParsed(parsed, prospect, liteScan);
    } catch (error) {
      console.error('Gemmetric strategy generation failed:', error);
      throw error;
    }
  }

  /**
   * GPT-5 Enhanced Helper Methods
   */

  /**
   * Enhanced national chain detection (GPT-5 recipe)
   */
  private isNationalChainGPT5(name: string, website?: string, category?: string): boolean {
    const nameLower = name.toLowerCase();
    
    // QSR chains
    const qsrChains = /\b(mcdonalds|burger king|wendys|subway|kfc|taco bell|dominos|pizza hut|chipotle|starbucks|dunkin|popeyes|chick fil a|chick-fil-a|five guys|shake shack|in n out|whataburger|culvers|sonic|arby|jack in the box|white castle|krystal|checkers|rallys|hardees|carls jr|del taco|el pollo loco|qdobas|moe|panera|jimmy johns|firehouse|potbelly|which wich|jersey mikes|penn station|charleys|hot head|noodles|boston market|kfc|taco bell|dominos|pizza hut|chipotle|starbucks|dunkin|popeyes|chick fil a|chick-fil-a|five guys|shake shack|in n out|whataburger|culvers|sonic|arby|jack in the box|white castle|krystal|checkers|rallys|hardees|carls jr|del taco|el pollo loco|qdobas|moe|panera|jimmy johns|firehouse|potbelly|which wich|jersey mikes|penn station|charleys|hot head|noodles|boston market)\b/i;
    
    // Retail chains
    const retailChains = /\b(walmart|target|home depot|lowes|best buy|staples|office depot|petco|petsmart|autozone|advance auto|oreilly|napa|harbor freight|goodwill|salvation army|sams club|winn dixie|dollar general|dollar tree|family dollar|big lots|ollies|ross|marshalls|tj maxx|homegoods|burlington|kohls|jcpenney|sears|macys|nordstrom|bloomingdales|saks|neiman marcus|barneys|bergdorf goodman|saks fifth avenue|nordstrom rack|saks off 5th|last call|outlet|factory|clearance)\b/i;
    
    // Gas stations
    const gasStations = /\b(shell|exxon|mobil|chevron|bp|texaco|sunoco|marathon|speedway|circle k|7-eleven|quik trip|kum & go|caseys|wawa|sheet|valero|citgo|phillips 66|conoco|76|arco|amoco|sinclair|gulf|marathon|speedway|circle k|7-eleven|quik trip|kum & go|caseys|wawa|sheet|valero|citgo|phillips 66|conoco|76|arco|amoco|sinclair|gulf)\b/i;
    
    // Banks
    const banks = /\b(bank of america|wells fargo|chase|citibank|us bank|regions bank|bb&t|truist|pnc|capital one|td bank|santander|keybank|fifth third|huntington|comerica|bmo harris|citizens bank|suntrust|bbva|hsbc|ally bank|discover bank|american express|capital one 360|chime|sofi|robinhood|webull|td ameritrade|charles schwab|fidelity|vanguard|etrade|merrill lynch|morgan stanley|goldman sachs|jpmorgan|bank of new york|state street|northern trust|bny mellon)\b/i;
    
    // Hotels
    const hotels = /\b(hilton|marriott|hyatt|ihg|wyndham|choice|best western|la quinta|motel 6|super 8|days inn|comfort inn|quality inn|econo lodge|rodeway inn|travelodge|howard johnson|ramada|baymont|wingate|tru|aloft|element|residence inn|towneplace|springhill|fairfield|courtyard|renaissance|conrad|waldorf|ritz carlton|four seasons|mandarin oriental|peninsula|shangri-la|aman|belmond|rosewood|st regis|w|edition|luxury collection|autograph|tribute|delta|canopy|moxy|ac|citizenm|yotel|pod|generator|freehand|the line|ace|standard|public|nomad|sydell|commune|collective|sonder|lyric|domio|stay alfred|turnkey|vacasa|airbnb|vrbo|homeaway|booking|expedia|orbitz|priceline|hotels|hotwire|trivago|kayak|skyscanner|momondo|google flights|hopper|kiwi|skypicker|dohop|jetradar|cheapflights|flightnetwork|gotogate|trip|agoda|hotelscombined|hoteltonight|lastminute|hotwire|priceline|orbitz|expedia|booking|airbnb|vrbo|homeaway|sonder|lyric|domio|stay alfred|turnkey|vacasa|collective|commune|sydell|nomad|public|standard|ace|the line|generator|freehand|pod|yotel|citizenm|ac|moxy|canopy|delta|tribute|autograph|luxury collection|waldorf|ritz carlton|four seasons|mandarin oriental|peninsula|shangri-la|aman|belmond|rosewood|st regis|w|edition|conrad|renaissance|courtyard|fairfield|springhill|towneplace|residence inn|element|aloft|tru|wingate|baymont|ramada|howard johnson|travelodge|rodeway inn|quality inn|comfort inn|econo lodge|days inn|super 8|motel 6|la quinta|best western|choice|wyndham|ihg|hyatt|marriott|hilton)\b/i;
    
    // Auto chains
    const autoChains = /\b(carmax|autonation|carvana|vroom|echopark|driveway|shift|carvana|vroom|echopark|driveway|shift|zoom|carvana|vroom|echopark|driveway|shift|zoom|carvana|vroom|echopark|driveway|shift|zoom)\b/i;
    
    // Storage chains
    const storageChains = /\b(public storage|extra space|life storage|cube smart|simply self storage|u haul|budget|penske|ryder|uhaul|budget|penske|ryder|uhaul|budget|penske|ryder|uhaul|budget|penske|ryder)\b/i;
    
    // Check all patterns
    return qsrChains.test(nameLower) || retailChains.test(nameLower) || gasStations.test(nameLower) || 
           banks.test(nameLower) || hotels.test(nameLower) || autoChains.test(nameLower) || storageChains.test(nameLower);
  }

  /**
   * Enhanced low-propensity category detection (GPT-5 recipe)
   */
  private isLowPropensityCategoryGPT5(category: string, types: string[]): boolean {
    const lowPropensityCategories = [
      'gas station', 'convenience store', 'storage', 'self storage', 'atm', 'automated teller',
      'government', 'school', 'university', 'college', 'library', 'park', 'post office', 
      'shipping', 'utility', 'religious org', 'cemetery', 'car wash', 'laundromat', 'parking',
      'big-box retail', 'dollar store', 'thrift store', 'pawn shop', 'check cashing', 'pharmacy',
      'liquor store', 'hotel', 'motel', 'fast food', 'quick service', 'qsr', 'drive thru',
      'bank', 'credit union', 'financial institution', 'nonprofit', 'non-profit', 'charity',
      'foundation', 'donation', 'resale', 'consignment', 'payday loan', 'title loan', 'cash advance',
      'money order', 'western union', 'moneygram', 'thrift', 'nonprofit', 'non-profit', 'charity',
      'foundation', 'donation', 'gas', 'fuel', 'convenience', 'storage', 'self storage', 'atm',
      'automated teller', 'government', 'city of', 'county of', 'state of', 'federal', 'post office',
      'dmv', 'library', 'park', 'shipping', 'utility', 'religious org', 'cemetery', 'car wash',
      'laundromat', 'parking', 'big-box retail', 'dollar store', 'thrift store', 'pawn shop',
      'check cashing', 'pharmacy', 'liquor store', 'hotel', 'motel', 'fast food', 'quick service',
      'qsr', 'drive thru', 'drive-through', 'bank', 'credit union', 'financial institution',
      'nonprofit', 'non-profit', 'charity', 'foundation', 'donation', 'resale', 'consignment',
      'payday loan', 'title loan', 'cash advance', 'money order', 'western union', 'moneygram'
    ];
    
    const allCategories = [...category.split(' '), ...types];
    return allCategories.some(cat => 
      lowPropensityCategories.some(lpc => cat.toLowerCase().includes(lpc))
    );
  }

  /**
   * Auto repair/tire/body ≠ auto dealer (GPT-5 recipe)
   */
  private isAutoRepairNotDealer(category: string, types: string[]): boolean {
    const repairCategories = [
      'auto repair', 'car repair', 'tire', 'tires', 'body shop', 'collision', 'auto service',
      'car service', 'oil change', 'lube', 'brake', 'transmission', 'engine', 'exhaust',
      'muffler', 'alignment', 'wheel', 'rim', 'battery', 'auto parts', 'car parts',
      'mechanic', 'garage', 'service center', 'maintenance', 'diagnostic', 'tune up',
      'inspection', 'emissions', 'smog', 'ac repair', 'heating repair', 'cooling repair'
    ];
    
    const allCategories = [...category.split(' '), ...types];
    return allCategories.some(cat => 
      repairCategories.some(repair => cat.toLowerCase().includes(repair))
    );
  }

  /**
   * Simplified category normalization (11 core categories) - GPT-5 recipe
   */
  private normalizeCategoryGPT5(categoryText: string): string {
    // Map to 11 core categories
    const categoryMap: { [key: string]: string } = {
      // Auto dealers
      'auto dealer': 'auto_dealer', 'car dealer': 'auto_dealer', 'automotive': 'auto_dealer',
      'car dealership': 'auto_dealer', 'dealership': 'auto_dealer',
      
      // Legal services
      'lawyer': 'legal_injury', 'attorney': 'legal_injury', 'legal': 'legal_injury',
      'personal injury': 'legal_injury', 'injury lawyer': 'legal_injury', 'accident': 'legal_injury',
      'trial': 'legal_injury', 'litigation': 'legal_injury',
      
      // Home services
      'hvac': 'hvac', 'heating': 'hvac', 'air conditioning': 'hvac', 'cooling': 'hvac',
      'plumbing': 'plumbing', 'roofing': 'roofing', 'home services': 'hvac',
      'contractor': 'hvac', 'electrician': 'hvac',
      
      // Medical/Dental
      'dentist': 'dental', 'dental': 'dental', 'orthodontist': 'orthodontics',
      'orthodontics': 'orthodontics', 'medical': 'medical', 'physician': 'medical',
      'doctor': 'medical', 'clinic': 'medical', 'hospital': 'medical', 'urgent care': 'medical',
      'plastic surgeon': 'medical', 'surgeon': 'medical', 'ortho': 'orthodontics',
      'implant': 'dental', 'dental implant': 'dental',
      
      // Furniture
      'furniture': 'furniture', 'furniture store': 'furniture', 'home furnishings': 'furniture',
      'mattress': 'furniture', 'sofa': 'furniture', 'bed': 'furniture',
      
      // Insurance
      'insurance': 'insurance', 'insurance agent': 'insurance', 'insurance broker': 'insurance',
      
      // Fitness
      'fitness': 'fitness', 'gym': 'fitness', 'health club': 'fitness', 'personal trainer': 'fitness',
      'workout': 'fitness', 'exercise': 'fitness',
      
      // Restaurants (distinguish QSR vs full-service)
      'cafe': 'restaurant_qsr', 'meal delivery': 'restaurant_qsr', 'fast food': 'restaurant_qsr',
      'quick service': 'restaurant_qsr', 'drive thru': 'restaurant_qsr', 'drive-through': 'restaurant_qsr',
      'qsr': 'restaurant_qsr', 'restaurant': 'restaurant_fullservice', 'dining': 'restaurant_fullservice',
      'food': 'restaurant_fullservice', 'eatery': 'restaurant_fullservice',
      
      // Nightlife
      'bar': 'bar', 'pub': 'bar', 'brewery': 'brewery', 'tavern': 'bar', 'lounge': 'bar',
      
      // Hotel chains
      'hotel': 'hotel_chain', 'motel': 'hotel_chain', 'inn': 'hotel_chain', 'lodge': 'hotel_chain',
      'resort': 'hotel_chain', 'accommodation': 'hotel_chain'
    };
    
    // Check for exact matches first
    for (const [noisy, normalized] of Object.entries(categoryMap)) {
      if (categoryText.includes(noisy)) {
        return normalized;
      }
    }
    
    return categoryText;
  }

  /**
   * Enhanced SpectrumFit Score (GPT-5 recipe)
   */
  private getSpectrumFitScoreGPT5(normalizedCategory: string): number {
    // Get 4-channel propensity vector
    const channelPropensity = this.getChannelPropensity(normalizedCategory);
    
    // Channel weights for Spectrum Reach mix
    const channelWeights = {
      CTV: 0.40,    // Connected TV (highest weight)
      OV: 0.25,     // Online Video
      Display: 0.15, // Display advertising
      SEM: 0.20     // Search Engine Marketing
    };
    
    // Calculate weighted SpectrumFit score
    return (channelPropensity.CTV * channelWeights.CTV) +
           (channelPropensity.OV * channelWeights.OV) +
           (channelPropensity.Display * channelWeights.Display) +
           (channelPropensity.SEM * channelWeights.SEM);
  }

  /**
   * Enhanced Presence Score (GPT-5 recipe with 95th percentile normalization)
   */
  private getPresenceScoreGPT5(prospect: ProspectData, normalizedCategory?: string): number {
    // Rating component: clamp01((min(rating,4.8)-3.5)/1.3)
    const ratingPart = prospect.rating ? Math.max(0, Math.min(1, (Math.min(prospect.rating, 4.8) - 3.5) / 1.3)) : 0;
    
    // Per-category review scaling: clamp01(log1p(reviews) / log1p(P95_reviews_in_normCategory))
    const qCat = prospect.reviewCount ? Math.max(0, Math.min(1, Math.log1p(prospect.reviewCount) / Math.max(1e-6, Math.log1p(this.getCategoryReviewPercentile(normalizedCategory))))) : 0;
    
    // Calculate presence score
    let presence = (prospect.website ? 0.25 : 0) + (prospect.phone ? 0.05 : 0) + 0.25 * ratingPart + 0.25 * qCat;
    
    // Nightlife/Hotel dampener (30% reduction)
    if (normalizedCategory && ['bar', 'brewery', 'restaurant_qsr', 'hotel_chain'].includes(normalizedCategory)) {
      presence *= 0.70;
    }
    
    // Cap at 0.80
    return Math.min(presence, 0.80);
  }

  /**
   * Enhanced Scale Score (GPT-5 recipe)
   */
  private getScaleScoreGPT5(prospect: ProspectData, normalizedCategory?: string): number {
    // Rating component: clamp01((min(rating,4.8)-3.5)/1.3)
    const ratingPart = prospect.rating ? Math.max(0, Math.min(1, (Math.min(prospect.rating, 4.8) - 3.5) / 1.3)) : 0;
    
    // Per-category review scaling: clamp01(log1p(reviews) / log1p(P95_reviews_in_normCategory))
    const qCat = prospect.reviewCount ? Math.max(0, Math.min(1, Math.log1p(prospect.reviewCount) / Math.max(1e-6, Math.log1p(this.getCategoryReviewPercentile(normalizedCategory))))) : 0;
    
    // Combine: 50% rating + 50% review volume
    return 0.5 * ratingPart + 0.5 * qCat;
  }

  /**
   * Enhanced Keyword Score (GPT-5 recipe)
   */
  private getKeywordScoreGPT5(name: string, category: string, types: string[]): number {
    const allText = `${name} ${category} ${types.join(' ')}`.toLowerCase();
    let score = 0;
    
    // Legal terms: injury|attorney|trial → +0.25
    const legalTerms = ['injury', 'attorney', 'trial', 'litigation', 'accident'];
    if (legalTerms.some(term => allText.includes(term))) score += 0.25;
    
    // Auto terms: chevrolet|toyota|ford|kia|hyundai|honda|nissan|auto → +0.20
    const autoTerms = ['chevrolet', 'toyota', 'ford', 'kia', 'hyundai', 'honda', 'nissan', 'auto', 'dealership'];
    if (autoTerms.some(term => allText.includes(term))) score += 0.20;
    
    // Med/Dental: ortho|dental|implant → +0.15
    const medicalTerms = ['ortho', 'dental', 'implant', 'dentist', 'medical', 'physician'];
    if (medicalTerms.some(term => allText.includes(term))) score += 0.15;
    
    // Home svcs: hvac|heating|cooling|plumb|roof → +0.15
    const homeServiceTerms = ['hvac', 'heating', 'cooling', 'plumb', 'roof', 'contractor'];
    if (homeServiceTerms.some(term => allText.includes(term))) score += 0.15;
    
    // Furniture: furniture|mattress → +0.10
    const furnitureTerms = ['furniture', 'mattress', 'sofa', 'bed'];
    if (furnitureTerms.some(term => allText.includes(term))) score += 0.10;
    
    // K = min(sum, 1.0)
    return Math.min(score, 1.0);
  }

  /**
   * Enhanced Distance Score with sigmoid function (GPT-5 recipe)
   */
  private getDistanceScoreGPT5(distance: number, radius: number = 3): number {
    if (distance === null || distance === undefined) return 0.55; // neutral center
    if (distance >= radius) return 0.20; // at radius edge
    
    const m = radius * 0.70; // midpoint where sigmoid = 0.5
    const k = 2.5 / radius; // slope: higher = steeper fall
    
    // raw sigmoid in [0,1]
    const s = 1 / (1 + Math.exp(k * (distance - m)));
    
    // Normalize to 0..1 range
    return (0.20 + 0.60 * s - 0.20) / 0.60;
  }

  public getCacheSize(): number {
    return this.businessCache.size;
  }

  /**
   * Calculate confidence based on data quality and completeness
   */
  private calculateConfidence(prospect: ProspectData, businessData: CachedBusinessData): number {
    let confidence = 0;
    
    // Data completeness (40% weight)
    let completenessScore = 0;
    if (prospect.name) completenessScore += 20;
    if (prospect.address) completenessScore += 20;
    if (prospect.website) completenessScore += 20;
    if (prospect.phone) completenessScore += 20;
    if (prospect.rating !== undefined) completenessScore += 10;
    if (prospect.reviewCount !== undefined) completenessScore += 10;
    confidence += (completenessScore / 100) * 40;
    
    // Category clarity (30% weight)
    let categoryScore = 0;
    if (prospect.category && prospect.category !== 'Other') categoryScore += 50;
    if (prospect.types && prospect.types.length > 0) categoryScore += 30;
    if (businessData.normalizedCategory && businessData.normalizedCategory !== 'other') categoryScore += 20;
    confidence += (categoryScore / 100) * 30;
    
    // Information quality (30% weight)
    let qualityScore = 0;
    if (prospect.rating && prospect.rating >= 4.0) qualityScore += 25;
    if (prospect.reviewCount && prospect.reviewCount >= 10) qualityScore += 25;
    if (prospect.website) qualityScore += 25;
    if (prospect.phone) qualityScore += 25;
    confidence += (qualityScore / 100) * 30;
    
    return Math.round(confidence);
  }
}

export default AIService.getInstance();
